import { api, mock_api, chatApi, getChatApiRoute } from "./api-config";
import { collectPaginatedItems } from "./collectPaginatedItems";
import { buildDocumentsListPayload } from "./documentsListPayload";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { useQuery, useMutation } from "@tanstack/react-query";
import { availableChatResponseTypes, formatTimeAgo } from "../utils/chat";
import { getPortalAccessLabel, getPortalAccessLabelFromVendor } from "../utils/roles";
import { buildBackendUserFilterParams } from "../utils/teamUsersBackend";
import {
  applyRfiToVqqDisplayToDocumentsRequestsResponse,
  applyRfiToVqqDisplayToGetRfisResponse,
} from "../utils/apiResponseRfiToVqqDisplay";
import { normalizeProviderOrgDetails } from "../utils/providerOrgStatus";
import { buildAdminAssessmentUpdateBody } from "../utils/assessmentAdminUpdateBody";

export const CheckEmailConfirmed = async () => {
  try {
    const response = await api.get(`/Identity/CheckEmailConfirmed`);
    return response.data;
  } catch (error) {
    console.error("Error fetching provider master user details:", error);
    throw error;
  }
};

export const getProvidersListForFilters = async ({ hideDiscoveryStatus = false } = { hideDiscoveryStatus: false }) => {
  const res = await api.get("/Providers/Filters/Providers");
  return res.data.filter((provider) => hideDiscoveryStatus ? provider.discovery_status === true : true);
};

export const getRfiListForFilters = async () => {
  const res = await api.get("RFI-Templates/Filters/RFITemplates");
  return res.data;
};
export const get_providers = {
  getAllProvider: async (body) => {
    try {
      const response = await api.post(`/Providers/Search`, body);
      return response.data;
    } catch (error) {
      console.error("Error fetching filtered providers:", error);
      throw error;
    }
  },
};
export const exportProvider = async (body) => {
  try {
    const response = await api.post(`/Providers/Export`, body);
    return response.data;
  } catch (error) {
    console.error("Error fetching filtered providers:", error);
    throw error;
  }
};

export const searchProvider = async (search_text = "") => {
  try {
    const res = await api.get(
      `/Providers/Filters/Search?search_text=${search_text}`
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const searchRfiTemplates = async (search_text = "") => {
  try {
    const res = await api.get(
      `/RFI-Templates/Filters/Search?search_text=${search_text}`
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getAllIndications = async () => {
  const res = await api.get("/Providers/Filters/Indications");
  return res.data;
};

export const getAllProviderTypes = async () => {
  const res = await api.get("/Providers/Filters/ProviderTypes");
  return res.data;
};
export const getAllCompanySize = async () => {
  const res = await api.get("/Providers/Filters/EmployeeCountRanges");
  return res.data;
};
export const getAnnualRevenueRange = async () => {
  const res = await api.get("/Providers/Filters/AnnualRevenueRange");
  return res.data;
};

export const getExportLogs = async (currentPage) => {
  const res = await api.get("/Utilities/Exports/Logs", {
    params: {
      page_size: 21,
      current_page: currentPage,
    },
  });
  return res.data;
};

export const getExportFiles = async (id) => {
  const res = await api.get("Utilities/Exports/File", {
    params: {
      id,
    },
  });
  return res.data;
};

export const getProviderBasicProfile = async (id) => {
  try {
    const response = await api.get(`/Providers/${id}/Basic-Profile`);
    return response.data;
  } catch (error) {
    console.error("Error fetching provider basic profile:", error);
    throw error;
  }
};

export const getProviderCompanyProfile = async (id) => {
  try {
    const response = await api.get(`/Providers/${id}/Company-Profile`);
    return response.data;
  } catch (error) {
    console.error("Error fetching provider company profile:", error);
    throw error;
  }
};

export const getProviderAttachments = async (id) => {
  const response = await api.get(`/Providers/${id}/Attachments`, {
    params: {
      page_size: 21,
      current_page: 1,
    },
  });
  return response.data;
};
export const getAttachmentFiles = async (id) => {
  const res = await api.get(`/Providers/${id.id}/Attachments/File`, {
    params: {
      attachment_id: id.attachmentId,
    },
  });
  return res.data;
};

export const getProviderDocuments = async (id) => {
  const response = await api.get(`/Providers/${id}/Documents`, {
    params: {
      current_page: 1,
      page_size: 21,
    },
  });
  return response.data;
};

export const DocumentsBookmark = async (body) => {
  try {
    const response = await api.post(`/Documents/${body.document_id}/Bookmark`);
    return response.data;
  } catch (error) {
    console.error("Error fetching filtered providers:", error);
    throw error;
  }
};

export const requestAccess = async (id, hcQuestionnaireId) => {
  try {
    const params =
      Number.isFinite(Number(hcQuestionnaireId)) && Number(hcQuestionnaireId) > 0
        ? { hc_questionnaire_id: Number(hcQuestionnaireId) }
        : undefined;
    const response = await api.post(`/Documents/${id}/Request`, {}, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching filtered providers:", error);
    throw error;
  }
};

export const getRequestBookmarkDetail = async (id) => {
  const response = await api.get(
    "Sponsors/Documents/BookmarkAndRequestStatus",
    {
      params: {
        document_id: id,
      },
    }
  );
  return response.data;
};

export const getAssessmentDocuments = async (currentPage) => {
  const response = await api.get("Assessments/Documents", {
    params: {
      current_page: currentPage,
      page_size: 21,
    },
  });
  return response.data;
};

export const getAllDocumentsList = async (
  currentPage,
  searchQuery,
  docType,
  provider,
  status,
  services,
  vqaId,
  entityId,
  service_names,
  page_size = 21,
  discoveryStatus = null,
  archiveStatusFilter = null,
  sortOptions = {},
  locationFilterParams = null,
  orgLibraryView = false
) => {
  try {
    const payload = buildDocumentsListPayload({
      currentPage,
      searchQuery,
      docType,
      provider,
      status,
      services,
      vqaId,
      entityId,
      service_names,
      page_size,
      discoveryStatus,
      archiveStatusFilter,
      sortOptions,
      locationFilterParams,
      orgLibraryView,
    });
    const response = await api.post("/Documents", payload);
    return response.data;
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};

export const getVendorLibraryServiceCategories = async (providerId) => {
  if (!providerId) {
    return { items: [] };
  }
  const response = await api.get(`/Documents/Provider/${providerId}/ServiceCategories`);
  return response.data ?? { items: [] };
};

export const advancedSearch = async (
  currentPage,
  searchQuery,
  docType,
  provider,
  status,
  services,
  vqaId
) => {
  try {
    const response = await api.post("/Documents/AdvancedSearch", {
      current_page: currentPage,
      page_size: 21,
      search_text: searchQuery,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};

export const getAllDocumentsRequestList = async (
  currentPage,
  docType,
  provider,
  status,
  services,
  bookmark,
  searchQuery,
  sponsor_id_list,
  service_names,
  page_size = 21,
  sortOptions = {},
  orgLibraryView = false
) => {
  try {
    const { orderBy, sortDirection } = sortOptions || {};
    const payload = {
      current_page: currentPage,
      page_size,
      document_type: docType,
      provider_id_list: provider,
      document_status: status,
      rfi_id_list: service_names && service_names.length > 0 ? [] : services,
      rfi_name: service_names || [],
      search_text: searchQuery == null ? "" : String(searchQuery),
      sponsor_id_list: sponsor_id_list,
      is_bookmarked:
        bookmark.length == 2 || bookmark.length == 0
          ? null
          : bookmark[0] == "Bookmarked"
          ? true
          : false,
      ...(orgLibraryView ? { org_library_view: true } : {}),
    };
    // FE contract: backend may honor `sort_by` / `sort_direction` when implemented.
    if (orderBy != null && orderBy !== "") {
      payload.sort_by = orderBy;
    }
    if (sortDirection === "asc" || sortDirection === "desc") {
      payload.sort_direction = sortDirection;
    }
    const response = await api.post("Documents/Requests", payload);
    // UI-only: document_name + capabilities[].rfi_name may contain "RFI" from API (see apiResponseRfiToVqqDisplay.js)
    return applyRfiToVqqDisplayToDocumentsRequestsResponse(response.data);
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};

const documentRequestListContainer = (response) =>
  response?.document_request_for_provider ||
  response?.document_request_for_sponsor;

/** Fetches all pages of Documents/Requests (no filters) to build Library Requests Services filter options. */
export const getAllDocumentsRequestListAllPages = async (pageSize = 100) => {
  const empty = [];
  return collectPaginatedItems({
    fetchPage: (page) =>
      getAllDocumentsRequestList(
        page,
        empty,
        empty,
        empty,
        empty,
        empty,
        "",
        undefined,
        undefined,
        pageSize
      ),
    getTotalPages: (r) => documentRequestListContainer(r)?.total_pages ?? 1,
    getItems: (r) => documentRequestListContainer(r)?.items ?? [],
    pageSize,
  });
};

export const providerRequestApprove = async (request_id) => {
  try {
    const response = await api.put(`Documents/Requests/${request_id}/Approve`);
    return response.data;
  } catch (error) {
    console.error("Error fetching filtered providers:", error);
    throw error;
  }
};

export const providerRequestReject = async (docId, reason) => {
  try {
    const response = await api.put(`Documents/Requests/${docId}/Reject`, {
      rejected_reason: reason,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching filtered providers:", error);
    throw error;
  }
};

export const getAllDocumentsRequestListAdmin = async (
  currentPage,
  docType,
  provider,
  status,
  services,
  service_names,
  sponsor_id_list,
  searchQuery = "",
  page_size = 21,
  sortOptions = {},
  archiveStatusFilter = null,
  orgLibraryView = false
) => {
  try {
    const { orderBy, sortDirection } = sortOptions || {};
    const payload = {
      current_page: currentPage,
      page_size,
      document_type: docType,
      provider_id_list: provider,
      document_status: status,
      rfi_id_list: service_names && service_names.length > 0 ? [] : services,
      rfi_name: service_names || [],
      sponsor_id_list: sponsor_id_list,
      search_text: searchQuery == null ? "" : String(searchQuery),
      ...(archiveStatusFilter && { archive_status_filter: archiveStatusFilter }),
      ...(orgLibraryView ? { org_library_view: true } : {}),
    };
    if (orderBy != null && orderBy !== "") {
      payload.sort_by = orderBy;
    }
    if (sortDirection === "asc" || sortDirection === "desc") {
      payload.sort_direction = sortDirection;
    }
    const response = await api.post("Documents/Admin/Requests", payload);
    // Same Library Requests row mapping as non-admin route (see apiResponseRfiToVqqDisplay.js)
    return applyRfiToVqqDisplayToDocumentsRequestsResponse(response.data);
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};

export const archiveDocumentRequest = async (requestId, archiveComment) => {
  const response = await api.put(
    `Documents/Requests/${requestId}/Archive`,
    { archive_comment: archiveComment }
  );
  return response.data;
};

export const unarchiveDocumentRequest = async (requestId) => {
  const response = await api.put(
    `Documents/Requests/${requestId}/Unarchive`
  );
  return response.data;
};

export const archiveDocument = async (documentId, archiveComment) => {
  const response = await api.put(`Documents/${documentId}/Archive`, {
    archive_comment: archiveComment,
  });
  return response.data;
};

export const unarchiveDocument = async (documentId, archiveComment) => {
  const response = await api.put(`Documents/${documentId}/Unarchive`, {
    archive_comment: archiveComment,
  });
  return response.data;
};

export const getDocumentActivity = async (documentId) => {
  const response = await api.get(`Documents/${documentId}/Activity`);
  return response.data;
};

export const archiveSponsorRfi = async (sponsorRfiResponseId, archiveComment) => {
  const response = await api.put(
    `RFI-Templates/SponsorRfi/${sponsorRfiResponseId}/Archive`,
    { archive_comment: archiveComment }
  );
  return response.data;
};

export const unarchiveSponsorRfi = async (sponsorRfiResponseId) => {
  const response = await api.put(
    `RFI-Templates/SponsorRfi/${sponsorRfiResponseId}/Unarchive`
  );
  return response.data;
};

export const archiveVqaRequest = async (vqaRequestId, archiveComment) => {
  const response = await api.put(
    `Assessments/Admin/Request/${vqaRequestId}/Archive`,
    { archive_comment: archiveComment }
  );
  return response.data;
};

export const unarchiveVqaRequest = async (vqaRequestId) => {
  const response = await api.put(
    `Assessments/Admin/Request/${vqaRequestId}/Unarchive`
  );
  return response.data;
};

export const getDocumentDetails = async (document_id) => {
  const response = await api.get(`Documents/${document_id}`);
  return response.data;
};
export const postAssessmentsRequests = async (
  providerId,
  providerType,
  isPrimaryLocationRequired,
  isSecondaryLocationRequired,
  secondaryLocationAddress,
  capabilities, // Array of { rfi_id, rfi_name } objects
  desiredInitialDraftDate,
  additionalDetails,
  isMultiSponsorAssessment,
  providerName,
  providerMainContactName,
  providerMainContactEmail,
  providerUrl,
  providerAddress1,
  providerCity,
  providerZip,
  providerCountry,
  providerPhoneNumber,
  providerAdditionalAddresses,
  providerAddress2,
  providerState,
  vqaType,
  otherAddress,
  is_provider_new,
  provider_type,
  provider_contacts
) => {
  try {
    const response = await api.post("Assessments/Request/Create", {
      vqa_location: {
        is_primary_location_required: isPrimaryLocationRequired,
        is_secondary_location_required: isSecondaryLocationRequired,
        secondary_location_address: secondaryLocationAddress,
      },
      capabilities: capabilities.map((capability) => ({
        rfi_id: capability.rfi_id,
        rfi_name: capability.rfi_name,
      })),
      desired_initial_draft_date: desiredInitialDraftDate,
      additional_details: additionalDetails,
      is_multi_sponsor_assessment: isMultiSponsorAssessment,
      provider_id: providerId,
      provider_type: providerType,
      provider_name: providerName,
      provider_contact_name: providerMainContactName,
      provider_contact_email: providerMainContactEmail,
      provider_url: providerUrl,
      provider_address1: providerAddress1,
      provider_city: providerCity,
      provider_zip: providerZip,
      provider_country: providerCountry,
      provider_phone_number: providerPhoneNumber,
      provider_additional_addresses: providerAdditionalAddresses,
      provider_address2: providerAddress2,
      provider_state: providerState,
      vqa_type: vqaType,
      other_address: otherAddress,
      is_provider_new,
      provider_type,
      provider_contacts,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};

export const getAuditorsForFilters = async () => {
  const res = await api.get("Assessments/Filters/Auditors");
  return res.data;
};

export const getAllVQAList = async (
  currentPage,
  auditor,
  provider,
  sponsor,
  status,
  services,
  vqaMonth,
  searchQuery,
  pageSize = 21,
  sortOptions = {}
) => {
  try {
    const { orderBy, sortDirection } = sortOptions || {};
    const payload = {
      current_page: currentPage,
      page_size: pageSize,
      auditor_id_list: auditor,
      provider_id_list: provider,
      sponsor_id_list: sponsor,
      vqa_status: status,
      rfi_id_list: services,
      vqa_month: vqaMonth ? vqaMonth : null,
      search: searchQuery ? searchQuery : "",
    };
    // FE contract: backend may honor `sort_by` / `sort_direction` when implemented (same idea as admin lists).
    if (orderBy != null && orderBy !== "") {
      payload.sort_by = orderBy;
    }
    if (sortDirection === "asc" || sortDirection === "desc") {
      payload.sort_direction = sortDirection;
    }
    const response = await api.post("Assessments/Request", payload);
    return response.data;
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};

/** Fetches all pages of user assessments (no service filter) for building complete services list */
export const getAllVQAListAllPages = async (
  auditor,
  provider,
  sponsor,
  status,
  vqaMonth,
  searchQuery,
  pageSize = 100
) => {
  return collectPaginatedItems({
    fetchPage: (page) =>
      getAllVQAList(
        page,
        auditor,
        provider,
        sponsor,
        status,
        [],
        vqaMonth,
        searchQuery,
        pageSize,
        {}
      ),
    getTotalPages: (r) => r?.total_pages ?? 1,
    getItems: (r) => r?.items ?? [],
    pageSize,
  });
};

export const getAllAssessmentsForCalendar = async (
  currentPage,
  auditorIdList,
  providerIdList,
  vqaStatus,
  rfiIdList,
  providerTypes,
  vqaMonth,
  providerType,
  assessmentType,
  searchQuery
) => {
  try {
    const response = await api.post("/Assessments", {
      current_page: currentPage,
      page_size: 100,
      auditor_id_list: auditorIdList,
      provider_id_list: providerIdList,
      vqa_status: vqaStatus,
      rfi_id_list: rfiIdList,
      provider_types: providerTypes,
      show_all_vqas: true,
      vqa_month: vqaMonth ? vqaMonth : null,
      provider_type: providerType ? providerType : null,
      assessment_type: assessmentType ? assessmentType : null,
      search_text: searchQuery ? searchQuery : "",
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching assessments:", error);
    throw error;
  }
};

export const getAllVQAListAdmin = async (
  currentPage,
  auditor,
  provider,
  status,
  services,
  sponsor,
  search,
  sortOptions = {}
) => {
  try {
    const { orderBy, sortDirection } = sortOptions || {};
    const payload = {
      current_page: currentPage,
      page_size: 21,
      auditor_id_list: auditor,
      provider_id_list: provider,
      vqa_status: status,
      rfi_id_list: services,
      sponsor_id_list: sponsor,
      search_text: search || null,
    };
    if (orderBy != null && orderBy !== "") {
      payload.sort_by = orderBy;
    }
    if (sortDirection === "asc" || sortDirection === "desc") {
      payload.sort_direction = sortDirection;
    }
    const response = await api.post("Assessments/Admin", payload);
    return response.data;
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};
export const getAllVQAListAdminRequest = async (
  currentPage,
  provider,
  status,
  services,
  sponsor,
  search,
  options
) => {
  try {
    const { sortOptions, requestTypeList } = options || {};
    const { orderBy, sortDirection } = sortOptions || {};
    const payload = {
      current_page: currentPage,
      page_size: 21,
      provider_id_list: provider,
      vqa_request_status: status,
      rfi_id_list: services,
      sponsor_id_list: sponsor,
      search: search || null,
    };
    if (orderBy != null && orderBy !== "") {
      payload.sort_by = orderBy;
    }
    if (sortDirection === "asc" || sortDirection === "desc") {
      payload.sort_direction = sortDirection;
    }
    // Server-side only — omit the key entirely when nothing is
    // selected rather than sending an empty array (matches vqa_request_status/etc above).
    if (requestTypeList?.length) {
      payload.request_type_list = requestTypeList;
    }
    const response = await api.post("Assessments/Admin/Request", payload);
    return response.data;
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};

export const getVQAActivity = async (vqa_id) => {
  const response = await api.get(`Assessments/${vqa_id}/Activity`, {
    params: {
      current_page: 1,
      page_size: 200,
    },
  });
  return response.data;
};
export const getRFIActivity = async (sponsorRfiResponseId) => {
  const response = await api.get(
    `RFI-Templates/${sponsorRfiResponseId}/Activity`,
    {
      params: {
        current_page: 1,
        page_size: 200,
      },
    }
  );
  return response.data;
};
export const getVQAActivityBeforeAccepting = async (vqa__request_id) => {
  const response = await api.get(
    `Assessments/Request/${vqa__request_id}/Activity`,
    {
      params: {
        current_page: 1,
        page_size: 200,
      },
    }
  );
  return response.data;
};

export const getVQADetails = async (vqa_id) => {
  const response = await api.get(`Assessments/Admin/Request/${vqa_id}`);
  return response.data;
};

export const adminRequestApprove = async (vqa_id) => {
  try {
    const response = await api.put(
      `Assessments/Admin/Request/${vqa_id}/Approve`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching filtered providers:", error);
    throw error;
  }
};
export const adminRequestReject = async (vqa_id, reason) => {
  try {
    const response = await api.put(
      `Assessments/Admin/Request/${vqa_id}/Reject`,
      {
        reason,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching filtered providers:", error);
    throw error;
  }
};

export const getBlobSasToken = async (uploadEntityType, uploadEntityId) => {
  try {
    const response = await api.get(`/Utilities/Blobs/UploadToken`, {
      params: {
        upload_entity_type: uploadEntityType,
        upload_entity_id: uploadEntityId,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching SAS token:", error);
    throw error;
  }
};

export const uploadFileToBlob = async (file, blobSasUrl) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    // Upload the file to the blob storage using the SAS URL
    await api.put(blobSasUrl, formData, {
      headers: {
        "Content-Type": file.type,
      },
    });

    return blobSasUrl; // Return the URL where the file is stored
  } catch (error) {
    console.error("Error uploading file to blob storage:", error);
    throw error;
  }
};
export const createDocument = async (documentData) => {
  try {
    const response = await api.post("/Documents/Admin/Create", documentData);
    return response.data;
  } catch (error) {
    console.error("Error creating document:", error);
    throw error;
  }
};

export const getDocumentFiles = async (id) => {
  const res = await api.get(`/Documents/${id}/File`);
  return res.data;
};
export const getAllDocumentFiles = async (ids) => {
  const res = await api.post(`Documents/Files`, {
    document_ids: ids,
  });
  return res.data;
};

export const addActivity = async (vqa_id, comment) => {
  try {
    const response = await api.post(`/Assessments/${vqa_id}/Activity`, {
      activity_comments: comment,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating document:", error);
    throw error;
  }
};

export const VQASponsorContactAdmin = async (vqa_id, sponsorContact) => {
  try {
    const response = await api.put(
      `/Assessments/Admin/${vqa_id}/SponsorContact`,
      {
        sponsor_id: sponsorContact?.sponsor_id,
        sponsor_contact_name: sponsorContact?.sponsor_contact_name,
        sponsor_contact_email: sponsorContact?.sponsor_contact_email,
        sponsor_contact_phone: sponsorContact?.sponsor_contact_phone,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating sponsor contact:", error);
    throw error;
  }
};

export const VQAProviderContactAdmin = async (vqa_id, providerContact) => {
  try {
    const response = await api.put(
      `/Assessments/Admin/${vqa_id}/ProviderContact`,
      {
        provider_contact_name: providerContact?.provider_contact_name,
        provider_contact_email: providerContact?.provider_contact_email,
        provider_contact_phone: providerContact?.provider_contact_phone,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating sponsor contact:", error);
    throw error;
  }
};

export const VQAAssessmentOverviewAdmin = async (
  vqa_id,
  overviewAssessment
) => {
  try {
    const response = await api.put(`/Assessments/Admin/${vqa_id}/Overview`, {
      desired_initial_draft_date:
        overviewAssessment?.desired_initial_draft_date,
      agreed_initial_draft_date: overviewAssessment?.agreed_initial_draft_date,
      assessment_start_date: overviewAssessment?.assessment_start_date,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating sponsor contact:", error);
    throw error;
  }
};

export const createAuditor = async (auditorData) => {
  try {
    const response = await api.post(`Assessments/Admin/Auditors/Create`, {
      first_name: auditorData.first_name,
      last_name: auditorData.last_name,
      office_phone: auditorData.office_phone,
      email_address: auditorData.email_address,
      country: auditorData.country,
      city: auditorData.city,
      state: auditorData.state,
      alternate_email_address: auditorData.alternate_email_address,
      company_affiliation: auditorData.company_affiliation,
      mobile_phone: auditorData.mobile_phone,
      training_status: auditorData.training_status,
      training_completion_date: auditorData.training_completion_date,
      cv_document_id: auditorData.cv_document_id,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching filtered providers:", error);
    throw error;
  }
};
export const updateAuditor = async (auditorId, auditorData) => {
  try {
    const response = await api.put(`Assessments/Admin/Auditors/${auditorId}`, {
      first_name: auditorData.first_name,
      last_name: auditorData.last_name,
      office_phone: auditorData.office_phone,
      email_address: auditorData.email_address,
      country: auditorData.country,
      city: auditorData.city,
      state: auditorData.state,
      alternate_email_address: auditorData.alternate_email_address,
      company_affiliation: auditorData.company_affiliation,
      mobile_phone: auditorData.mobile_phone,
      training_status: auditorData.training_status,
      training_completion_date: auditorData.training_completion_date,
      cv_document_id: auditorData.cv_document_id,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching filtered providers:", error);
    throw error;
  }
};

export const getAuditorDetails = async (id) => {
  const response = await api.get(`Assessments/Admin/Auditors/${id}`);
  return response.data;
};

export const deleteAuditor = async (id) => {
  const response = await api.delete(`Assessments/Admin/Auditors/${id}`);
  return response.data;
};

export const getAllAuditors = async () => {
  try {
    const response = await api.post(`Assessments/Admin/Auditors`, {
      page_size: 100,
      current_page: 1,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating document:", error);
    throw error;
  }
};

export const getProvidersListForProviderDetails = async () => {
  const res = await api.get("/Providers/Filters/Assessments");
  return res.data;
};
export const getProvidersListForProviderUserDetails = async (id) => {
  const res = await api.get(`Providers/Users/${id}/Details`);
  const data = res.data;
  if (!data) return data;
  const jobTitle =
    data.job_title ??
    data.title ??
    data.jobTitle ??
    data.JobTitle ??
    data.Title ??
    "";
  return {
    ...data,
    job_title: jobTitle,
  };
};
export const getProvidersListForTherapeutic = async () => {
  const res = await api.get("Providers/TherapeuticAreas");
  return res.data;
};

export const getProviderDetailsWithUsers = async (providerId, orgId) => {
  const res = await api.get(`/providers/details`, {
    params: {
      providerId,
      orgId,
    },
  });
  return res.data;
};

export const getProviderAllData = async (providerId) => {
  try {
    const response = await api.get(`/Sponsors/${providerId}/Users`);
    return response.data;
  } catch (error) {
    console.error("Error fetching provider all data:", error);
    throw error;
  }
};

export const getSwitchToV1 = async () => {
  const res = await api.get("/Identity/Auth/V1Signin", {
    method: "GET",
    headers: {
      "Cache-Control": "no-cache",
    },
  });
  return res.data;
};

export const updateBackendAssessmentDetails = async (vqaId, vqaData) => {
  try {
    const response = await api.put(
      `Assessments/Admin/${vqaId}`,
      buildAdminAssessmentUpdateBody(vqaData)
    );

    return response.data;
  } catch (error) {
    console.error("Error updating assessment details:", error);
    throw error;
  }
};

export const getAssessmentRequestContactsByVqa = async (vqaId) => {
  const res = await api.get(`Assessments/Request/Contacts/ByVqa/${vqaId}`);
  return res.data;
};

export const getAdditionalScopeOptions = async () => {
  try {
    const response = await api.get("Assessments/AdditionalScopeOptions");
    return response.data;
  } catch (error) {
    console.error("Error fetching additional scope options:", error);
    return [];
  }
};

export const getVQARequestDetailsAdmin = async (id) => {
  const res = await api.get(`Assessments/Admin/Request/${id}`);
  return res.data;
};
export const getVQADetailsAdmin = async (id) => {
  const res = await api.get(`Assessments/Admin/${id}`);
  return res.data;
};
export const getAssessmentDetails = async (id) => {
  const res = await api.get(`Assessments/Request/${id}`);
  return res.data;
};
export const getJoinVAQDetails = async (id) => {
  const res = await api.get(`Assessments/${id}`);
  return res.data;
};
export const getJoinVAQRequestDetails = async (id) => {
  const res = await api.get(`Assessments/JoinRequest/${id}`);
  return res.data;
};

export const joinVqaRequest = async (
  id,
  additional_info,
  selectedServices,
  desiredInitialDraftDate
) => {
  try {
    const response = await api.post(`/Assessments/${id}/Join`, {
      additional_info,
      secondary_capabilitie_dto: selectedServices,
      // Optional, matches JoinVqaAdditionalInfo.desired_initial_draft_date.
      ...(desiredInitialDraftDate
        ? { desired_initial_draft_date: desiredInitialDraftDate }
        : {}),
    });
    return response.data;
  } catch (error) {
    console.error("Error joining VQA request:", error);
    throw error;
  }
};

/** Admin's per-join Details instance, keyed by join_vqa_request_id. */
export const getJoinVQARequestDetailsAdmin = async (id) => {
  const res = await api.get(`/Assessments/Admin/JoinRequest/${id}`);
  return res.data;
};

/** All-or-nothing — moves a join's requested add-ons to confirmed. */
export const confirmAddOnScope = async (id) => {
  const response = await api.post(
    `/Assessments/Admin/JoinRequest/${id}/ConfirmAddOnScope`
  );
  return response.data;
};

/** Admin edit-save on a confirmed join sub-request's own scope. */
export const updateJoinVqaScope = async (id, updateJoinVqaScopeDto) => {
  const response = await api.put(
    `/Assessments/Admin/JoinRequest/${id}/Scope`,
    updateJoinVqaScopeDto
  );
  return response.data;
};
export const getVQAParticipantsList = async (
  id,
  { current_page = 1, page_size = 10, status } = {}
) => {
  const response = await api.get(`/Assessments/Admin/${id}/JoinRequest`, {
    params: {
      current_page,
      page_size,
      ...(status ? { status } : {}),
    },
  });
  return response.data;
};

export const getJoinRequestListAdmin = async ({
  current_page = 1,
  page_size = 10,
  status,
} = {}) => {
  const response = await api.get("/Assessments/Admin/JoinRequest", {
    params: {
      current_page,
      page_size,
      ...(status ? { status } : {}),
    },
  });
  return response.data;
};
export const acceptVqaRequest = async (id) => {
  try {
    const response = await api.post(
      `/Assessments/Admin/JoinRequest/${id}/Approve`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching filtered providers:", error);
    throw error;
  }
};
export const rejectVqaRequest = async (id) => {
  try {
    const response = await api.post(
      `/Assessments/Admin/JoinRequest/${id}/Reject`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching filtered providers:", error);
    throw error;
  }
};
export const getAllRequestedAssesmentList = async (
  currentPage,
  auditor,
  provider,
  status,
  services,
  sponsor
) => {
  try {
    const response = await api.post("Assessments/JoinRequest", {
      current_page: currentPage,
      page_size: 21,
      auditor_id_list: auditor,
      provider_id_list: provider,
      vqa_status: status,
      rfi_id_list: services,
      sponsor_id_list: sponsor,
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};

export const getAssesmentRequestDetails = async (id) => {
  const res = await api.get(`/Assessments/JoinRequest/${id}`);
  return res.data;
};
export const generateQuestionnaire = async ({ rfiFile1Name, rfiFile2Name }) => {
  try {
    const response = await api.post("/vqa_poc/generate_questionnaire", {
      rfiFile1Name: rfiFile1Name,
      rfiFile2Name: rfiFile2Name,
    });

    return response.data;
  } catch (error) {
    console.error("API error details:", {
      response: error.response?.data,
      status: error.response?.status,
      body: error.config?.data,
    });
    throw error;
  }
};

export const getInitialBotResponse = async (id) => {
  try {
    const response = await api.get(`/Vqa_Poc/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching initial bot response:", error);
    throw error;
  }
};

export const sendQuestionResponse = async (questionId, answer) => {
  try {
    const response = await api.post(
      `/Vqa_Poc/question_response/${questionId}`,
      {
        questionId: questionId,
        answer: answer,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error sending question response:", error);
    throw error;
  }
};
export const generateAudit = async (id) => {
  try {
    const response = await api.post(`/Vqa_Poc/generate_audit/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error sending question response:", error);
    throw error;
  }
};

export const getAllBackendUserList = async (currentPage, search_text, sortOptions = {}) => {
  try {
    const { orderBy, sortDirection } = sortOptions || {};
    const response = await api.get("/Identity/BackendUsers", {
      params: {
        current_page: currentPage,
        page_size: 21,
        search_text,
        ...(orderBy != null && orderBy !== ""
          ? { sort_by: orderBy, sort_direction: sortDirection }
          : {}),
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};
export const addBackendUser = async (userData) => {
  try {
    const response = await api.post(`Identity/BackendUsers`, userData);
    return response.data;
  } catch (error) {
    console.error("Error adding backend user:", error);
    throw error;
  }
};

export const getBackendUserDetails = async (id) => {
  try {
    const response = await api.get(`/Identity/BackendUsers/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching backend user details:", error);
    throw error;
  }
};

export const updateBackendUser = async (id, userData) => {
  try {
    const response = await api.patch(`/Identity/BackendUsers/${id}`, userData);
    return response.data;
  } catch (error) {
    console.error("Error updating backend user:", error);
    throw error;
  }
};

export const deleteBackendUser = async (id) => {
  try {
    const response = await api.delete(`/Identity/BackendUsers/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting backend user:", error);
    throw error;
  }
};

export const getAllSponsorUserList = async ({
  currentPage,
  id,
  search_text,
  page_size = 21,
  sortOptions = {},
  filters,
}) => {
  try {
    const { orderBy, sortDirection } = sortOptions || {};
    const response = await api.get(`/Identity/Sponsor/SponsorUser/${id}`, {
      params: {
        current_page: currentPage,
        page_size,
        search_text,
        ...buildBackendUserFilterParams(filters),
        ...(orderBy != null && orderBy !== ""
          ? { sort_by: orderBy, sort_direction: sortDirection }
          : {}),
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching sponsor users:", error);
    throw error;
  }
};
export const getAllSponsorUsersList = async ({
  currentPage,
  userSearchQuery,
  pageSize = 21,
  sortOptions = {},
  filters,
}) => {
  try {
    const { orderBy, sortDirection } = sortOptions || {};
    const response = await api.get(`/Identity/SponsorUsers`, {
      params: {
        current_page: currentPage,
        page_size: pageSize,
        search_text: userSearchQuery,
        ...buildBackendUserFilterParams(filters),
        ...(orderBy != null && orderBy !== ""
          ? { sort_by: orderBy, sort_direction: sortDirection }
          : {}),
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching sponsor users:", error);
    throw error;
  }
};

export const getSponsorUserDetails = async (id) => {
  try {
    const response = await api.get(`/Identity/SponsorUsers/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching sponsor user details:", error);
    throw error;
  }
};

export const updateSponsorUser = async (id, userData) => {
  try {
    const response = await api.patch(`/Identity/SponsorUsers/${id}`, userData);
    return response.data;
  } catch (error) {
    console.error("Error updating sponsor user:", error);
    throw error;
  }
};
export const updateProviderUser = async (id, userData) => {
  try {
    const response = await api.patch(`/Identity/ProviderUsers/${id}`, userData);
    return response.data;
  } catch (error) {
    console.error("Error updating provider user:", error);
    throw error;
  }
};

export const updateProviderUserRolesPermissions = async (id, roleData) => {
  try {
    const response = await api.patch(
      `/Identity/SponsorUsers/${id}/RolesPermissions`,
      roleData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating provider user roles and permissions:", error);
    throw error;
  }
};

export const updateProviderUserRolesPermissionsForProvider = async (
  id,
  roleData
) => {
  try {
    const response = await api.patch(
      `/Identity/ProviderUsers/${id}/RolesPermissions`,
      roleData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating provider user roles and permissions:", error);
    throw error;
  }
};
export const deleteSponsorMasterUser = async (id) => {
  try {
    const response = await api.delete(`/Identity/SponsorMasterUsers/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting backend user:", error);
    throw error;
  }
};
export const deleteSponsorUser = async (id) => {
  try {
    const response = await api.delete(`/Identity/SponsorUsers/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting backend user:", error);
    throw error;
  }
};

export const addSponsorUser = async (userData) => {
  try {
    const response = await api.post("/Identity/SponsorUsers", userData);
    return response.data;
  } catch (error) {
    console.error("Error adding sponsor user:", error);
    throw error;
  }
};

export const getMyProfile = async () => {
  try {
    const response = await api.get("/Identity/BackendUsers/Me");
    return response.data;
  } catch (error) {
    console.error("Error fetching my profile:", error);
    throw error;
  }
};

export const updateMyProfile = async (userData) => {
  try {
    const response = await api.patch("/Identity/BackendUsers/Me", userData);
    return response.data;
  } catch (error) {
    console.error("Error updating my profile:", error);
    throw error;
  }
};

export const updateBackendUserMfaStatus = async (payload) => {
  try {
    const response = await api.patch(
      "/Identity/BackendUsers/Me/MfaStatus",
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating backend user MFA status:", error);
    throw error;
  }
};

// Get Sponsor Master Users List
export const getSponsorMasterUsers = async ({ pageSize, currentPage }) => {
  try {
    const response = await api.get(
      `/Identity/SponsorMasterUsers?page_size=${pageSize}&current_page=${currentPage}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching sponsor master users:", error);
    throw error;
  }
};

// Get Sponsor Master User Details
export const getSponsorMasterUserDetails = async (id) => {
  try {
    const response = await api.get(`/Identity/SponsorMasterUsers/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching sponsor master user details:", error);
    throw error;
  }
};

export const getAllSponsorsList = async () => {
  try {
    const response = await api.get("/Sponsors/AllSponsors", {});
    return response.data;
  } catch (error) {
    console.error("Error fetching sponsor list for filters:", error);
    throw error;
  }
};

// Create Sponsor Master User
export const addSponsorMasterUser = async (userData) => {
  try {
    const response = await api.post("/Identity/SponsorMasterUsers", userData);
    return response.data;
  } catch (error) {
    console.error("Error adding sponsor master user:", error);
    throw error;
  }
};

// Update Sponsor Master User
export const updateSponsorMasterUser = async (id, userData) => {
  try {
    const response = await api.patch(
      `/Identity/SponsorMasterUsers/${id}`,
      userData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating sponsor master user:", error);
    throw error;
  }
};

export const getSponsorUserProfile = async () => {
  try {
    const response = await api.get("/Identity/SponsorUsers/Me");
    return response.data;
  } catch (error) {
    console.error("Error fetching sponsor user profile:", error);
    throw error;
  }
};

export const updateSponsorUserMfaStatus = async (payload) => {
  try {
    const response = await api.patch(
      "/Identity/SponsorUsers/Me/MfaStatus",
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating sponsor user MFA status:", error);
    throw error;
  }
};

// Get Sponsor Master User Profile (Me)
export const getSponsorMasterUserProfile = async () => {
  try {
    const response = await api.get("/Identity/SponsorMasterUsers/Me");
    return response.data;
  } catch (error) {
    console.error("Error fetching sponsor master user profile:", error);
    throw error;
  }
};

// Update Sponsor User Profile (Me)
export const updateSponsorUserProfile = async (userData) => {
  try {
    const response = await api.patch("/Identity/SponsorUsers/Me", userData);
    return response.data;
  } catch (error) {
    console.error("Error updating sponsor user profile:", error);
    throw error;
  }
};

// Update Sponsor Master User Profile (Me)
export const updateSponsorMasterUserProfile = async (userData) => {
  try {
    const response = await api.patch(
      "/Identity/SponsorMasterUsers/Me",
      userData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating sponsor master user profile:", error);
    throw error;
  }
};

export const getProviderMasterUsers = async (currentPage, pageSize = 10) => {
  try {
    const response = await api.get("/Identity/ProviderMasterUsers", {
      params: {
        current_page: currentPage,
        page_size: pageSize,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching provider master users:", error);
    throw error;
  }
};

export const createProviderMasterUser = async (userData) => {
  try {
    const response = await api.post("/Identity/ProviderUsers", userData);
    return response.data;
  } catch (error) {
    console.error("Error creating provider master user:", error);
    throw error;
  }
};

export const getProviderMasterUserDetails = async (id) => {
  try {
    const response = await api.get(`/Identity/ProviderUsers/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching provider master user details:", error);
    throw error;
  }
};

export const updateProviderMasterUser = async (id, userData) => {
  try {
    const response = await api.patch(`/Identity/ProviderUsers/${id}`, userData);
    return response.data;
  } catch (error) {
    console.error("Error updating provider master user:", error);
    throw error;
  }
};

export const deleteProviderMasterUser = async (id) => {
  try {
    const response = await api.delete(`/Identity/ProviderUsers/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting provider master user:", error);
    throw error;
  }
};

// export const requestCancelAssessment = async (vqaRequestId) => {
//   try {
//     const response = await api.put(
//       `Assessments/Request/${vqaRequestId}/Cancel`
//     );
//     return response.data;
//   } catch (error) {
//     console.error("Error fetching filtered providers:", error);
//     throw error;
//   }
// };
// export const CancelAssessment = async (vqaId) => {
//   try {
//     const response = await api.put(`Assessments/${vqaId}/Cancel`);
//     return response.data;
//   } catch (error) {
//     console.error("Error fetching filtered providers:", error);
//     throw error;
//   }
// };
export const CancelAssessment = async (vqaId, sponsor, reason) => {
  try {
    const response = await api.put(`Assessments/Admin/${vqaId}/Cancel`, {
      sponsor_Id: sponsor?.sponsor_id,
      sponsor_name: sponsor?.sponsor_name,
      reason,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching filtered providers:", error);
    throw error;
  }
};
export const getAllSponsorsForAdmin = async (payload) => {
  try {
    const response = await api.post("/Sponsors/Admin", payload);
    return response.data;
  } catch (error) {
    console.error("Error fetching sponsors:", error);
    throw error;
  }
};
export const getParentSponsorDetails = async (id) => {
  try {
    const response = await api.get(`/Sponsors/Admin/${id}`);

    return response.data;
  } catch (error) {
    console.error("Error fetching sponsors:", error);
    throw error;
  }
};

export const createSponsor = async (sponsorData) => {
  try {
    const response = await api.post("/Sponsors/Admin/Create", sponsorData);
    return response.data;
  } catch (error) {
    console.error("Error creating sponsor:", error);
    throw error;
  }
};

export const createSponsorAdminDualRole = async (sponsorData) => {
  try {
    const response = await api.post(
      "/Sponsors/Admin/DualRole/Create",
      sponsorData
    );
    return response.data;
  } catch (error) {
    console.error("Error creating sponsor admin dual role:", error);
    throw error;
  }
};

// Add this new method to api-methods.js
export const getProvidersPaginated = async ({
  pageSize,
  currentPage,
  providerIdFilter,
  rfiTemplateIdFilter,
}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    queryParams.append("page_size", pageSize);
    queryParams.append("current_page", currentPage);

    // Add provider ID filters if present
    if (providerIdFilter?.length) {
      providerIdFilter.forEach((id) => {
        queryParams.append("provider_id_filter", id);
      });
    }

    // Add RFI template ID filters if present
    if (rfiTemplateIdFilter?.length) {
      rfiTemplateIdFilter.forEach((id) => {
        queryParams.append("rfi_template_id_filter", id);
      });
    }

    const response = await mock_api.get(`/Providers?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching paginated providers:", error);
    throw error;
  }
};

export const createProvider = async (providerData) => {
  try {
    const response = await api.post("/Providers/Create", providerData);
    return response.data;
  } catch (error) {
    console.error("Error creating provider:", error);
    throw error;
  }
};
export const createProviderUser = async (providerData) => {
  try {
    const response = await api.post("Identity/ProviderUsers", providerData);
    return response.data;
  } catch (error) {
    console.error("Error creating provider:", error);
    throw error;
  }
};

export const createProviderDualRole = async (providerData) => {
  try {
    const response = await api.post(
      "Sponsors/Admin/DualRole/Create",
      providerData
    );
    return response.data;
  } catch (error) {
    console.error("Error creating provider dual role:", error);
    throw error;
  }
};

export const createProviderDualRoleFromProvider = async (providerData) => {
  try {
    const response = await api.post("Providers/DualRole/Create", providerData);
    return response.data;
  } catch (error) {
    console.error("Error creating provider dual role from provider:", error);
    throw error;
  }
};

export const updateProviderBasicProfile = async (id, data) => {
  try {
    const response = await mock_api.patch(
      `/Providers/${id}/Basic-Profile`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error updating provider basic profile:", error);
    throw error;
  }
};

export const updateProviderCompanyProfile = async (id, data) => {
  try {
    const response = await mock_api.patch(
      `/Providers/${id}/Company-Profile`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error updating provider company profile:", error);
    throw error;
  }
};

export const updateProviderEmail = async (id, email) => {
  try {
    const response = await api.patch(`/Providers/${id}/email`, {
      email,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating provider email:", error);
    throw error;
  }
};

export const updateSponsorUserDetails = async (id, userData) => {
  try {
    const response = await api.patch(`/Sponsors/Admin/${id}`, userData, {
      headers: {
        "Content-Type": "application/json", // Added header to specify JSON payload
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error updating provider master user:", error);
    throw error;
  }
};

export const getSponsorDetails = async (id) => {
  try {
    const response = await api.get(`/Sponsors/Admin/${id}`);

    return response.data;
  } catch (error) {
    console.error("Error fetching sponsors:", error);
    throw error;
  }
};

export const getSponsorsDetails = async (sponsorId, orgId) => {
  try {
    const response = await api.get(`/sponsors/details`, {
      params: {
        sponsorId,
        orgId,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching sponsor details:", error);
    throw error;
  }
};
export const deleteSponsorUserDetails = async (id) => {
  try {
    const response = await api.delete(`/Sponsors/Admin/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error updating provider master user:", error);
    throw error;
  }
};

export const userMetrics = async (id) => {
  try {
    const response = await api.get(`Assessments/RequestMatrics/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const listPurchaserVendors = async () => {
  try {
    const response = await api.get(`/RFI-Templates/purchaser_vendor`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const listOfQuestionnaireTemplate = async () => {
  try {
    const response = await api.get(`/RFI-Templates/questionnaire_templates`, {
      params: {
        include_global: true,
        include_template: false,
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/** Admin Library page: same templates as sponsor flow, but uses admin HC token (no sponsor_id in JWT). */
export const listOfQuestionnaireTemplateForAdmin = async () => {
  try {
    const response = await api.get(
      `/RFI-Templates/admin/questionnaire_templates`,
      {
        params: {
          include_global: true,
          include_template: false,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const questionnaireTemplateDetails = async (questionnaire_id) => {
  try {
    const response = await api.get(
      `/RFI-Templates/questionnaires/${questionnaire_id}`,
      {
        params: {
          include_questions: false,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const sendRfiToPurchaseVendor = async (id, data) => {
  try {
    const response = await api.post(
      `/RFI-Templates/purchaser_vendor/${id}/questionnaires`,
      data
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const createPurchaseVendors = async (data) => {
  try {
    const response = await api.post(`Providers/purchaser_vendor`, data);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const createPurchaseVendorForSponsorIfNotExists = async (data) => {
  try {
    const response = await api.post(
      `Providers/purchase-vendor/ForEachSponsor`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const ensureVendorHyperComplyOrganization = async (providerId) => {
  try {
    const response = await api.post(`Providers/HyperComplyOrganization/${providerId}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const getRfisAdmin = async (
  currentPage,
  searchQuery,
  provider,
  templates,
  status,
  services,
  sponsor_id_list,
  sortOptions = {},
  dueDateFilter = null,
  pageSize = 21
) => {
  try {
    const { orderBy, sortDirection } = sortOptions || {};
    const payload = {
      current_page: currentPage,
      page_size: pageSize,
      auditor_id_list: [],
      provider_id_list: provider,
      rfi_status: status,
      services_id_list: [...(services || []), ...(templates || [])],
      search_text: searchQuery ? searchQuery : "",
      sponsor_id_list: sponsor_id_list,
    };
    if (orderBy != null && orderBy !== "") {
      payload.sort_by = orderBy;
    }
    if (sortDirection === "asc" || sortDirection === "desc") {
      payload.sort_direction = sortDirection;
    }
    if (dueDateFilter?.startDate) {
      payload.due_by_start = dueDateFilter.startDate;
    }
    if (dueDateFilter?.endDate) {
      payload.due_by_end = dueDateFilter.endDate;
    }
    const response = await api.post(`/RFI-Templates/admin/get_rfis`, payload);
    // UI-only: backend may still echo "RFI" in name/services; normalize for tables (see apiResponseRfiToVqqDisplay.js)
    return applyRfiToVqqDisplayToGetRfisResponse(response.data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const getRfis = async (
  currentPage,
  pageSize,
  searchQuery,
  provider,
  templates,
  status,
  services,
  vendors,
  sortOptions = {},
  dueDateFilter = null
) => {
  try {
    const { orderBy, sortDirection } = sortOptions || {};
    const payload = {
      current_page: currentPage,
      page_size: pageSize,
      auditor_id_list: [], // Not used for templates
      vendors: provider,
      rfi_status: status,
      services_id_list: [...(services || []), ...(templates || [])], // Include template IDs (hash_ids) here
      search_text: searchQuery ? searchQuery : "",
      provider_id_list: vendors,
    };
    if (orderBy != null && orderBy !== "") {
      payload.sort_by = orderBy;
    }
    if (sortDirection === "asc" || sortDirection === "desc") {
      payload.sort_direction = sortDirection;
    }
    if (dueDateFilter?.startDate) {
      payload.due_by_start = dueDateFilter.startDate;
    }
    if (dueDateFilter?.endDate) {
      payload.due_by_end = dueDateFilter.endDate;
    }
    const response = await api.post(`/RFI-Templates/get_rfis`, payload);
    // UI-only: normalize RFI → VQQ in list rows for grids (see apiResponseRfiToVqqDisplay.js)
    return applyRfiToVqqDisplayToGetRfisResponse(response.data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/** Fetches all pages of user RFIs (no template filter) for building complete template list */
export const getMyRfisAllPages = async (
  searchQuery,
  provider,
  status,
  services,
  vendors,
  pageSize = 100
) => {
  return collectPaginatedItems({
    fetchPage: (page) =>
      getRfis(
        page,
        pageSize,
        searchQuery,
        provider,
        [],
        status,
        services,
        vendors,
        {}
      ),
    getTotalPages: (r) => r?.total_pages ?? 1,
    getItems: (r) => r?.items ?? [],
    pageSize,
  });
};

/** Diligent Admin Library: `get_rfis` requires sponsor_id in token; this uses `admin/get_rfis` instead. */
export const getAdminRfisAllPages = async (
  searchQuery = "",
  provider = [],
  status = [],
  services = [],
  sponsorIdList = [],
  pageSize = 100
) => {
  return collectPaginatedItems({
    fetchPage: (page) =>
      getRfisAdmin(
        page,
        searchQuery,
        provider,
        [],
        status,
        services,
        sponsorIdList,
        {}
      ),
    getTotalPages: (r) => r?.total_pages ?? 1,
    getItems: (r) => r?.items ?? [],
    pageSize,
  });
};

export const getVendorRfis = async (
  currentPage,
  pageSize,
  searchQuery,
  sponsor,
  provider,
  docType,
  status,
  services,
  providers,
  sortOptions = {},
  dueDateFilter = null
) => {
  try {
    const { orderBy, sortDirection } = sortOptions || {};
    const payload = {
      current_page: currentPage,
      page_size: pageSize,
      sponsor_id_list: sponsor,
      auditor_id_list: docType,
      vendors: provider,
      rfi_status: status,
      services_id_list: services,
      search_text: searchQuery ? searchQuery : "",
      provider_id_list: providers,
    };
    if (orderBy != null && orderBy !== "") {
      payload.sort_by = orderBy;
    }
    if (sortDirection === "asc" || sortDirection === "desc") {
      payload.sort_direction = sortDirection;
    }
    if (dueDateFilter?.startDate) {
      payload.due_by_start = dueDateFilter.startDate;
    }
    if (dueDateFilter?.endDate) {
      payload.due_by_end = dueDateFilter.endDate;
    }
    const response = await api.post(`/RFI-Templates/Provider/get_rfis`, payload);
    // UI-only: same row shape as get_rfis — normalize "RFI" in name/services for provider dashboards (see apiResponseRfiToVqqDisplay.js)
    return applyRfiToVqqDisplayToGetRfisResponse(response.data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/** All pages of provider/vendor RFIs with no template filter (for Templates dropdown options). */
export const getVendorRfisAllPages = async (
  searchQuery = "",
  sponsor = [],
  provider = [],
  docType = [],
  status = [],
  pageSize = 100
) => {
  return collectPaginatedItems({
    fetchPage: (page) =>
      getVendorRfis(
        page,
        pageSize,
        searchQuery,
        sponsor,
        provider,
        docType,
        status,
        [],
        [],
        {}
      ),
    getTotalPages: (r) => r?.total_pages ?? 1,
    getItems: (r) => r?.items ?? [],
    pageSize,
  });
};

/** All pages of the sponsor grid with its full filter/sort set, for exports. */
export const getMyRfisAllPagesForExport = async ({
  searchQuery = "",
  provider = [],
  templates = [],
  status = [],
  services = [],
  vendors = [],
  sortOptions = {},
  dueDateFilter = null,
  pageSize = 100,
} = {}) => {
  return collectPaginatedItems({
    fetchPage: (page) =>
      getRfis(
        page,
        pageSize,
        searchQuery,
        provider,
        templates,
        status,
        services,
        vendors,
        sortOptions,
        dueDateFilter
      ),
    getTotalPages: (r) => r?.total_pages ?? 1,
    getItems: (r) => r?.items ?? [],
    pageSize,
  });
};

/** All pages of the admin grid with its full filter/sort set, for exports. */
export const getAdminRfisAllPagesForExport = async ({
  searchQuery = "",
  provider = [],
  templates = [],
  status = [],
  services = [],
  sponsorIdList = [],
  sortOptions = {},
  dueDateFilter = null,
  pageSize = 100,
} = {}) => {
  return collectPaginatedItems({
    fetchPage: (page) =>
      getRfisAdmin(
        page,
        searchQuery,
        provider,
        templates,
        status,
        services,
        sponsorIdList,
        sortOptions,
        dueDateFilter,
        pageSize
      ),
    getTotalPages: (r) => r?.total_pages ?? 1,
    getItems: (r) => r?.items ?? [],
    pageSize,
  });
};

/** All pages of the vendor grid with its full filter/sort set, for exports. */
export const getVendorRfisAllPagesForExport = async ({
  searchQuery = "",
  sponsor = [],
  provider = [],
  docType = [],
  status = [],
  services = [],
  providers = [],
  sortOptions = {},
  dueDateFilter = null,
  pageSize = 100,
} = {}) => {
  return collectPaginatedItems({
    fetchPage: (page) =>
      getVendorRfis(
        page,
        pageSize,
        searchQuery,
        sponsor,
        provider,
        docType,
        status,
        services,
        providers,
        sortOptions,
        dueDateFilter
      ),
    getTotalPages: (r) => r?.total_pages ?? 1,
    getItems: (r) => r?.items ?? [],
    pageSize,
  });
};

export const getProvidersLocation = async (id) => {
  try {
    const response = await api.get(`Providers/${id}/locations`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const getPurchaseVendorBySponsor = async (id) => {
  try {
    const response = await api.get(`Providers/PurchaseVendorBySponsor/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const getRFIsDetails = async (id) => {
  try {
    const response = await api.get(`/RFI-Templates/get_rfis/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/** Story 14534 — replace vendor contacts on VQQ Overview. */
export const updateVqqVendorContacts = async (sponsorRfiResponseId, contacts) => {
  const response = await api.put(
    `/RFI-Templates/${sponsorRfiResponseId}/VendorContacts`,
    { contacts },
  );
  return response.data;
};

/** Story 14534 — replace sponsor contacts on VQQ Overview. */
export const updateVqqSponsorContacts = async (sponsorRfiResponseId, contacts) => {
  const response = await api.put(
    `/RFI-Templates/${sponsorRfiResponseId}/SponsorContacts`,
    { contacts },
  );
  return response.data;
};

/**
 * Story 14534 — resend invite to saved vendor/sponsor contacts.
 * @param {string} sponsorRfiResponseId
 * @param {{ contact_type: 'vendor'|'sponsor', emails?: string[] }} payload
 */
export const resendVqqContactInvites = async (sponsorRfiResponseId, payload) => {
  const response = await api.post(
    `/RFI-Templates/${sponsorRfiResponseId}/ResendContactInvites`,
    payload,
  );
  return response.data;
};

/** Story 14580 — replace vendor contacts on Assessment Overview. */
export const updateVqaVendorContacts = async (id, contacts) => {
  const response = await api.put(`/Assessments/${id}/VendorContacts`, {
    contacts,
  });
  return response.data;
};

/** Story 14580 — replace sponsor contacts on Assessment Overview. */
export const updateVqaSponsorContacts = async (id, contacts, sponsor_id) => {
  const response = await api.put(`/Assessments/${id}/SponsorContacts`, {
    contacts,
    sponsor_id,
  });
  return response.data;
};

/**
 * Story 14580 — resend invite to saved Assessment vendor/sponsor contacts.
 * @param {string} id
 * @param {{ contact_type: 'vendor'|'sponsor', emails?: string[], sponsor_id?: string }} payload
 */
export const resendVqaContactInvites = async (id, payload) => {
  const response = await api.post(
    `/Assessments/${id}/ResendContactInvites`,
    payload,
  );
  return response.data;
};

export const sponsorRFIMetrics = async (sponsorId = null) => {
  console.log({ sponsorId })
  try {
    const response = await api.get(`RFI-Templates/SponsorRfiMetrics${sponsorId ? `?sponsor_id=${sponsorId}` : ''}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const providerTotalCount = async () => {
  try {
    const response = await api.get(`Providers/ProviderTotalCount
 `);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const totalAssessment = async () => {
  try {
    const response = await api.get(`Assessments/TotalAssessment`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// sponsor assessment metrics
export const assessmentMetrics = async (sponsorId = null) => {
  try {
    const response = await api.get(`Assessments/AssessmentMetrics${sponsorId ? `?sponsor_id=${sponsorId}` : ''}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const providerRFIMetrics = async (providerId = null) => {
  try {
    const response = await api.get(`RFI-Templates/ProviderRfiMetrics${providerId ? `?provider_id=${providerId}` : ''}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const totalRFIs = async () => {
  try {
    const response = await api.get(`RFI-Templates/TotalRfiQuestionnaire`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const sponsorTotalCount = async () => {
  try {
    const response = await api.get(`/Sponsors/SponsorTotalCount`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const providerAssessmentMetrics = async (providerId = null) => {
  try {
    const response = await api.get(`Assessments/Provider/AssessmentMetrics${providerId ? `?provider_id=${providerId}` : ''}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const providerDiversityClassification = async () => {
  try {
    const response = await api.get(`Providers/DiversityClassification`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const providerCertifications = async () => {
  try {
    const response = await api.get(`Providers/Certifications`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const providerGeographicRegion = async () => {
  try {
    const response = await api.get(`Providers/GeographicRegion`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const providerAffiliatedOrganization = async () => {
  try {
    const response = await api.get(`Providers/AffiliatedOrganization`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const providerLanguages = async () => {
  try {
    const response = await api.get(`Providers/Languages`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const getClientSecrets = async () => {
  try {
    const response = await api.get(`/Identity/clientsecret`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const getProviderOrgDetails = async (id) => {
  try {
    const response = await api.get(`Providers/${id}`);
    return normalizeProviderOrgDetails(response.data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const updateProviderOrg = async (id, data) => {
  try {
    const response = await api.patch(`Providers/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const getAllProviderUsers = async (
  pageSize,
  current_page,
  search_text,
  sortOptions = {},
  filters
) => {
  try {
    const { orderBy, sortDirection } = sortOptions || {};
    const response = await api.get(`Providers/Users`, {
      params: {
        pageSize,
        pageNumber: current_page,
        search: search_text,
        ...buildBackendUserFilterParams(filters),
        ...(orderBy != null && orderBy !== ""
          ? { sort_by: orderBy, sort_direction: sortDirection }
          : {}),
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Maps API user object to the UI shape expected by TeamManagement / UsersListTable.
 * When the real endpoint is available, adjust the right-hand side to match API field names
 * (e.g. apiUser.firstName -> first_name, apiUser.subRoles -> sponsor_subrole_dto).
 */
export function mapVendorUserFromApi(apiUser) {
  if (!apiUser) return null;
  const roleDto = apiUser.sponsor_subrole_dto ?? apiUser.sub_roles ?? apiUser.vendor_subrole_dto ?? [];
  const roleLabel = Array.isArray(roleDto)
    ? roleDto.map((r) => r.sub_role ?? r.role ?? r).filter(Boolean).join(" / ")
    : apiUser.role ?? "";
  return {
    ...apiUser,
    id: apiUser.id ?? apiUser.provider_user_id,
    first_name: apiUser.first_name ?? apiUser.firstName ?? "",
    last_name: apiUser.last_name ?? apiUser.lastName ?? "",
    job_title: apiUser.job_title ?? apiUser.title ?? apiUser.jobTitle ?? "",
    title: apiUser.title ?? apiUser.job_title ?? apiUser.jobTitle ?? "",
    email: apiUser.email ?? "",
    role: roleLabel || apiUser.role,
    sponsor_subrole_dto: Array.isArray(roleDto) ? roleDto : [{ sub_role: roleLabel || apiUser.role }],
    is_active: apiUser.is_active ?? apiUser.isActive ?? true,
    status: apiUser.status ?? (apiUser.is_active !== false ? "Active" : "Inactive"),
    portal_access: getPortalAccessLabelFromVendor(apiUser),
    created_at: apiUser.created_at ?? apiUser.createdAt ?? apiUser.created ?? apiUser?.created_date ?? apiUser?.create_date_time ?? "",
    last_activity_at: apiUser.last_activity_at ?? apiUser.lastActivityAt ?? apiUser.last_login_at ?? apiUser.lastlogin ?? "",
    last_login_at: apiUser.last_login_at ?? apiUser.lastlogin ?? apiUser.last_activity_at ?? "",
    request_contact: apiUser.request_contact ?? apiUser.requestContact ?? "",
  };
}

export const getProviderUserByEmail = async (email) => {
  const response = await api.get("Providers/Users/email", {
    params: { email: email?.trim() },
  });
  return response.data;
};

export const getAllProviderUsersByProviderId = async (
  providerId,
  pageSize,
  current_page,
  search_text,
  sortOptions = {},
  filters
) => {
  try {
    const { orderBy, sortDirection } = sortOptions || {};
    const response = await api.get(`Providers/Users/${providerId}`, {
      params: {
        pageSize,
        pageNumber: current_page,
        search: search_text,
        ...buildBackendUserFilterParams(filters),
        ...(orderBy != null && orderBy !== ""
          ? { sort_by: orderBy, sort_direction: sortDirection }
          : {}),
      },
    });
    const raw = response.data;
    const items = Array.isArray(raw) ? raw : raw?.items ?? [];
    return {
      ...(Array.isArray(raw) ? { items: raw } : raw),
      items: items.map(mapVendorUserFromApi),
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const getAllProviderOrg = async (
  pageSize,
  current_page,
  search_text,
  sortOptions = {}
) => {
  try {
    const { orderBy, sortDirection } = sortOptions || {};
    const response = await api.get(`Providers`, {
      params: {
        page_size: pageSize,
        current_page,
        search_text,
        ...(orderBy != null && orderBy !== ""
          ? { sort_by: orderBy, sort_direction: sortDirection }
          : {}),
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const resendQuestionnaire = async (id) => {
  try {
    const response = await api.post(`RFI-Templates/ResendQuestionnaire/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const activateQuestionnaire = async (id) => {
  try {
    const response = await api.post(`RFI-Templates/ActivateQuestionnaire/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const cancelQuestionnaireRequest = async ({ id, sponsorRfiResponseId } = {}) => {
  try {
    const questionnaireId = Number(id);
    if (Number.isFinite(questionnaireId) && questionnaireId > 0) {
      const response = await api.post(
        `RFI-Templates/RevokeQuestionnaire/${questionnaireId}`
      );
      return response.data;
    }

    if (sponsorRfiResponseId) {
      const response = await api.post(
        `RFI-Templates/RevokeQuestionnaire/BySponsorRfiResponse/${sponsorRfiResponseId}`
      );
      return response.data;
    }

    throw new Error("Questionnaire id is missing for this VQQ.");
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const getSponsorsFilter = async () => {
  try {
    const response = await api.get(`/Sponsors/AllSponsors`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// 13876 Autorelease — eligibility + action endpoints. Both are keyed by sponsor_rfi_response_id
// (Guid) because pre-acceptance rows may not yet have an HC questionnaire_id assigned.
export const getAutoreleaseEligibility = async (sponsorRfiResponseId) => {
  const response = await api.get(
    `RFI-Templates/Autorelease/Eligibility/${sponsorRfiResponseId}`
  );
  return response.data;
};

export const postAutorelease = async (sponsorRfiResponseId, body) => {
  const response = await api.post(
    `RFI-Templates/Autorelease/${sponsorRfiResponseId}`,
    body
  );
  return response.data;
};

export const acceptVendorQuestionnaire = async (questionnaire_id, data) => {
  try {
    const response = await api.post(
      `RFI-Templates/AcceptQuestionnaire/${questionnaire_id}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const exportQuestionnaireExcel = async (data) => {
  try {
    const response = await api.post(`RFI-Templates/ExportQuestionnaire`, data);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getRfiSnapshot = async (questionnaireId) => {
  const response = await api.get(`/Documents/Rfi/Snapshot/${questionnaireId}`);
  return response.data;
};

export const postRequestRemediation = async (questionnaireId, payload) => {
  const response = await api.post(`/RFI-Templates/RequestRemediation/${questionnaireId}`, payload);
  return response.data;
};

export const downloadExportFile = async (folder, questionnaire_name) => {
  const response = await api.get(`/Utilities/Exports/Questionnaire`, {
    params: {
      folder,
      questionnaire_name,
    },
  });
  return response.data;
};
export const downloadExportDocs = async (
  folder,
  questionnaire_name,
  exclude_response_base_name = null,
) => {
  const params = {
    folder,
    questionnaire_name,
  };
  if (exclude_response_base_name) {
    params.exclude_response_base_name = exclude_response_base_name;
  }
  const response = await api.get(`Utilities/Exports/Zip/Questionnaire`, {
    params,
  });
  return response.data;
};

export const getLegacyAttachmentAvailability = async (
  folder,
  response_base_name,
) => {
  const response = await api.get(
    `/Utilities/Exports/Questionnaire/LegacyAttachments/Availability`,
    {
      params: {
        folder,
        response_base_name,
      },
    },
  );
  return response.data;
};

export const getExportStatus = async (exportLogsId) => {
  const response = await api.get(`/Utilities/Exports/Status/${exportLogsId}`);
  return response.data;
};

export const getFirstTimeSignin = async () => {
  try {
    const response = await api.get(`/Identity/TermsAccepted/Me`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
export const getVendorsMe = async () => {
  try {
    const response = await api.get(`Identity/ProviderUsers/Me`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updateProviderUserProfile = async (userData) => {
  try {
    const response = await api.patch(`/Identity/ProviderUsers/Me`, userData);
    return response.data;
  } catch (error) {
    console.error("Error updating provider user profile:", error);
    throw error;
  }
};

export const updateProviderUserMfaStatus = async (payload) => {
  try {
    const response = await api.patch(
      "/Identity/ProviderUsers/Me/MfaStatus",
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating provider user MFA status:", error);
    throw error;
  }
};

export const acceptTerms = async (id) => {
  try {
    const response = await api.patch(`/Identity/TermsAccepted`);
    return response.data;
  } catch (error) {
    console.error("Error updating backend user:", error);
    throw error;
  }
};

export const getHcSignInToken = async () => {
  try {
    const response = await api.get(`/Identity/Hc/SignInToken`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const hypercomplyLogout = async (data) => {
  try {
    const response = await api.post(`Identity/Hc/Logout`, data);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const supportingDocsExportVQA = async (entityId, entityType) => {
  try {
    const response = await api.get(`Documents/document/by-vqa`, {
      params: {
        entityId,
        entityType,
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const checkProvidersRequiredFields = async (id) => {
  try {
    const response = await api.get(`Providers/CheckFieldsNull`);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getSponsorCount = async (id) => {
  try {
    const response = await api.get(
      `/RFI-Templates/Provider/${id}/DistinctSponsorCountByProvider`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching provider sponsor count:", error);
    throw error;
  }
};

export const getProviderCounts = async () => {
  try {
    const response = await api.get("/Documents/ProviderCounts");
    return response.data;
  } catch (error) {
    console.error("Error fetching provider counts:", error);
    throw error;
  }
};

export const getProviderDocumentCountsByIds = async (providerIds, sponsorId = null) => {
  try {
    const response = await api.post("/Documents/ProviderCounts", {
      provider_ids: providerIds,
      sponsor_id: sponsorId,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching provider document counts:", error);
    throw error;
  }
};

export const getRfiResponse = async (rfiResponseId) => {
  try {
    const response = await api.get(
      `/RFI-Templates/V1/RfiResponse/${rfiResponseId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching RFI response:", error);
    throw error;
  }
};

export const getRfiResponseFile = async (rfiResponseId, extension) => {
  try {
    const response = await api.get("/Utilities/V1/RfiResponse/File", {
      params: {
        rfiResponseId,
        extension,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching RFI response file:", error);
    throw error;
  }
};

export const DocumentServicePoc = async () => {
  const res = await api.post(
    `DocumentServicePocApiControllerBase/generate_questionnaire`,
    {
      rfiFileName1:
        "https://storageeyxz2nh5nlydc.blob.core.windows.net/vqa-poc/RFI_AleraBiotech_Bioanalytical",
      rfiFileName2:
        "https://storageeyxz2nh5nlydc.blob.core.windows.net/vqa-poc/RFI_AleraBiotech_Bioanalytical",
    }
  );
  return res.data;
};
export const DocumentServicePocGet = async () => {
  const res = await api.get(
    `DocumentServicePocApiControllerBase/45904E7B-CBA9-47FB-BE92-75438EEE1B3B`
  );
  return res.data;
};

const getSummaryKeyMetric = (metric) => {
  switch (metric) {
    case "Trials":
      return "trials";
    case "Patients":
      return "total_patients";
    case "Therapeutic Areas":
      return "therapeutic_areas";
    case "Indications":
      return "unique_indications";
    default:
      return "trials";
  }
};

export const getTAIndicationsData = async (pagination, filters) => {
  try {
    const response = await api.post("/ClinicalExperience/Query", {
      vendor_ids: filters?.selectedVendors?.map((v) => v.value),
      therapeutic_area_ids: Object.values(filters?.selectedTAIndications).map(
        (ta) => ta.ta_code
      ),
      indication_ids: Object.values(filters?.selectedTAIndications)
        .map((ta) => ta.indications.map((i) => i.id))
        .flat(),
      service_category_ids: filters?.selectedCategories?.map(
        (cat) => cat.value
      ),
      vendor_type_ids: filters?.selectedVendorType?.map((d) => d.value),
      document_availability: filters?.selectedDocAvailability?.map((d) =>
        d.value === "rfi" ? "RfiAvailable" : "AuditReportAvailable"
      ),
      employee_ranges: filters.selectedEmployeeSizes?.map((d) => d.value),
      annual_revenue_ranges: filters.selectedRevenueRanges?.map((d) => d.value),
      country_list:
        filters.selectedLocationFilters
          .map((filter) =>
            filter.country?.value === "US" ? "US" : filter.country?.label
          )
          .filter((f) => f) || [],
      state_list:
        filters.selectedLocationFilters
          .map((filter) => filter.state?.label)
          .filter((f) => f) || [],
      city_list:
        filters.selectedLocationFilters
          .map((filter) => filter.city)
          .filter((f) => f) || [],
      columns: [
        "number_of_trials_in_indications",
        "number_of_phase_1_trials",
        "number_of_phase_1_healthy_volunteers",
        "number_of_phase_1_patients",
        "number_of_phase_2_trials",
        "number_of_phase_2_patients",
        "number_of_phase_3_trials",
        "number_of_phase_3_patients",
      ],
      page: pagination.page,
      page_size: pagination.size?.toString(),
      sort: [
        {
          column: "vendor_name",
          direction: "asc",
        },
        {
          column: "therapeutic_area",
          direction: "asc",
        },
        {
          column: "indication",
          direction: "asc",
        },
      ],
    });

    const parsedData =
      response.data?.items?.map((item) => ({
        CRO: item.vendor_name,
        "Therapeutic Area": item.therapeutic_area,
        Indication: item.indication,
        "Number of Trials in Indication":
          item.metrics.number_of_trials_in_indications ?? 0,
        "Number of Phase 1 Trials": item.metrics.number_of_phase_1_trials ?? 0,
        "Number of Phase 1 Healthy Volunteers":
          item.metrics.number_of_phase_1_healthy_volunteers ?? 0,
        "Number of Phase 1 Patients":
          item.metrics.number_of_phase_1_patients ?? 0,
        "Number of Phase 2 Trials": item.metrics.number_of_phase_2_trials ?? 0,
        "Number of Phase 2 Patients":
          item.metrics.number_of_phase_2_patients ?? 0,
        "Number of Phase 3 Trials": item.metrics.number_of_phase_3_trials ?? 0,
        "Number of Phase 3 Patients":
          item.metrics.number_of_phase_3_patients ?? 0,
      })) || [];

    return {
      data: parsedData,
      pagination: {
        ...pagination,
        total: response.data.total_pages,
      },
    };
  } catch (error) {
    console.error("Error fetching TA Indications response:", error);
    throw error;
  }
};
export const getTASummaryData = async (pagination, filters) => {
  try {
    const summarySelectedTA = filters.summaryFilters.selectedTA;
    const thereIsSelectedTA = summarySelectedTA && summarySelectedTA !== "All";
    const response = await api.post("/ClinicalExperience/Query", {
      vendor_ids: filters?.selectedVendors?.map((v) => v.value),
      therapeutic_area_ids: thereIsSelectedTA
        ? [summarySelectedTA]
        : Object.values(filters?.selectedTAIndications).map((ta) => ta.ta_code),
      indication_ids: Object.values(filters?.selectedTAIndications)
        .map((ta) => ta.indications.map((i) => i.id))
        .flat(),
      service_category_ids: filters?.selectedCategories?.map(
        (cat) => cat.value
      ),
      vendor_type_ids: filters?.selectedVendorType?.map((d) => d.value),
      document_availability: filters?.selectedDocAvailability?.map((d) =>
        d.value === "rfi" ? "RfiAvailable" : "AuditReportAvailable"
      ),
      employee_ranges: filters.selectedEmployeeSizes?.map((d) => d.value),
      annual_revenue_ranges: filters.selectedRevenueRanges?.map((d) => d.value),
      country_list:
        filters.selectedLocationFilters
          .map((filter) =>
            filter.country?.value === "US" ? "US" : filter.country?.label
          )
          .filter((f) => f) || [],
      state_list:
        filters.selectedLocationFilters
          .map((filter) => filter.state?.label)
          .filter((f) => f) || [],
      city_list:
        filters.selectedLocationFilters
          .map((filter) => filter.city)
          .filter((f) => f) || [],

      summary_key_metric: getSummaryKeyMetric(filters.summaryFilters.keyMetric),
      columns: [
        "number_of_trials_in_indications",
        "number_of_phase_1_trials",
        "number_of_phase_1_healthy_volunteers",
        "number_of_phase_1_patients",
        "number_of_phase_2_trials",
        "number_of_phase_2_patients",
        "number_of_phase_3_trials",
        "number_of_phase_3_patients",
      ],
      page: pagination.page,
      page_size: pagination.size?.toString(),
      sort: [
        {
          column: "vendor_name",
          direction: "asc",
        },
        {
          column: "therapeutic_area",
          direction: "asc",
        },
        {
          column: "indication",
          direction: "asc",
        },
      ],
    });

    const parsedSummary =
      response.data?.vendor_summary?.reduce((acc, summary) => {
        acc[summary.vendor_name] = {
          id: summary?.vendor_id,
          logo: summary?.logo,
          indicationsCount: summary?.unique_indication_count || 0,
          therapeuticAreasCount: summary?.therapeutic_area_count || 0,
          totalTrials: summary?.key_metric_value || 0,
          totalPatients: summary?.total_patients || 0,
          therapeuticAreas: summary?.top_areas || [],
        };
        return acc;
      }, {}) || {};

    return {
      data: parsedSummary,
      pagination: {
        ...pagination,
        total: response.data.total_pages,
      },
    };
  } catch (error) {
    console.error("Error fetching Summary response:", error);
    throw error;
  }
};
export const updateTokenRole = async (id, role, sub_role, rights) => {
  try {
    // Helper to generate random GUIDs for id fields
    const generateGuid = () =>
      "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });

    const response = await api.patch(`/Identity/${id}/tokenupdate`, {
      role,
      provider_user_subrole: (sub_role || []).map((role) => ({
        subroleId: generateGuid(),
        subrole: role,
      })),
      provider_user_permissions: (rights || []).map((right) => ({
        permissionId: generateGuid(),
        permission: right,
      })),
    });
    return response.data;
  } catch (error) {
    console.error("Error updating token role:", error);
    throw error;
  }
};

const getActionData = (jsonData) => {
  if (jsonData?.type === availableChatResponseTypes.trial_experience_card) {
    return {
      content: jsonData.answer,
      actionData: {
        title: jsonData.header.title,
        vendors: jsonData.vendors.map(({ provider_id }) => provider_id),
        comparison: jsonData.vendors.reduce((acc, vendor) => {
          acc[vendor.provider_name] = {
            id: vendor.provider_id,
            logo: vendor.logo_url,
            trials: vendor.trials,
            patients: vendor.patients,
            indications: vendor.indications,
          };
          return acc;
        }, {}),
        summary: jsonData.ai_summary,
      },
    };
  }

  if (jsonData?.type === availableChatResponseTypes.rfi_comparison_card) {
    return {
      actionData: {
        templateName: jsonData.header.section_name,
        vendors: jsonData.header.provider_names,
        question: jsonData.header.question_text,
        responses: jsonData.vendor_responses.map((vRes) => ({
          rfiId: vRes.questionnaire_id,
          vendorId: vRes.provider_id,
          vendorName: vRes.provider_name,
          response: vRes.response_text,
        })),
        summary: jsonData.ai_summary,
      },
    };
  }

  if (jsonData?.type === availableChatResponseTypes.rfi_download_card) {
    return {
      content: jsonData.header.description || jsonData.answer,
      actionData: {
        rfis: jsonData.rfis.map((rfi) => ({
          id: rfi.questionnaire_id,
          status: rfi.rfi_status,
          vendor: rfi.provider_name,
          vendorId: rfi.provider_id,
          name: rfi.template_name,
          templateId: rfi.template_id,
          filepath: rfi.file_url || rfi.filepath,
        })),
      },
    };
  }

  if (
    jsonData?.type === availableChatResponseTypes.vendor_profile_discovery_card
  ) {
    return {
      content: jsonData.header?.description || jsonData.answer,
      highlightTerms: [],
      // TODO: adjust the badgeType
      badgeType:
        jsonData.vendors?.[0]?.badges?.service_categories?.length > 0
          ? "serviceCategories"
          : "therapeuticAreas",
      actionData:
        jsonData.vendors?.map((vendor) => ({
          vendorType: vendor.vendor_type,
          id: vendor.provider_id,
          name: vendor.provider_name,
          logo: vendor.logo_url,
          location: vendor.location,
          serviceCategories: vendor.badges?.service_categories || [],
          therapeuticAreas: vendor.badges?.therapeutic_areas || [],
          bio: vendor.bio_quote,
        })) || [],
      otherRelevantVendors:
        jsonData.other_vendors?.map((vendor) => ({
          id: vendor.provider_id,
          name: vendor.provider_name,
          reason: vendor.summary,
        })) || [],
      totalResults: jsonData.total_results,
      excelFileBase64: jsonData.excel_file_base64,
      excelFileName: jsonData.excel_file_name,
    };
  }
  return {};
};

const parseChatResponse = (data) => {
  let response = {};
  let jsonData = {};
  const content = data.answer || data.text;

  // Check if content is already an object (no need to parse)
  if (typeof content === "object" && content !== null) {
    jsonData = content;
  } else {
    // Try to parse if it's a string
    try {
      jsonData = JSON.parse(
        content
          .replace(/\r\n|\n/, "")
          .replace("```json", "")
          .replace("```", "")
      );
    } catch {
      jsonData = {};
    }
  }

  if (jsonData?.type && jsonData?.type !== "text") {
    response = {
      actionType: jsonData?.type || "text",
      content: "",
      answer: "",
      text: "",
      ...getActionData(jsonData),
    };
  }
  return response;
};

// TODO: remove mock url for chat endpoints (working in local and without gateway)
// Chat service axios instance is now in api-config.js to avoid circular dependencies
// chatApi is imported at the top of the file

export const getChatHistory = async () => {
  try {
    const response = await chatApi.get(getChatApiRoute("/chat/sessions"));
    const chatHistory = response.data.map((session) => ({
      ...session,
      timeAgo: formatTimeAgo(new Date(session.createdAt)),
    }));

    return chatHistory;
  } catch (error) {
    console.error("Error fetching chat history response:", error);
    if (
      error.code === "ERR_NETWORK" ||
      error.message === "Network Error" ||
      error.code === "ERR_EMPTY_RESPONSE"
    ) {
      const chatServiceUrl = chatApi.defaults.baseURL;
      console.error(`⚠️ Cannot connect to backend at ${chatServiceUrl}`);
      console.error(`   Verify that the server is running on that port.`);
      console.error(
        `   You can test by accessing: ${chatServiceUrl}/swagger/index.html`
      );
    }
    throw error;
  }
};

export const sendChat = async ({ sessionId, userPrompt }) => {
  try {
    const response = await chatApi.post(getChatApiRoute("/chat"), { sessionId, userPrompt });
    const responseData = parseChatResponse(response.data);
    const aiMessage = {
      id: (Date.now() + 1).toString(),
      role: "assistant",
      sessionId: response.data.sessionId,
      content: responseData.answer || response.data.answer,
      timestamp: new Date(),
      ...responseData,
    };

    return aiMessage;
  } catch (error) {
    console.error("Error fetching chat response:", error);
    if (
      error.code === "ERR_NETWORK" ||
      error.message === "Network Error" ||
      error.code === "ERR_EMPTY_RESPONSE"
    ) {
      const chatServiceUrl = chatApi.defaults.baseURL;
      console.error(`⚠️ Cannot connect to backend at ${chatServiceUrl}`);
      console.error(`   Verify that the server is running on that port.`);
      console.error(
        `   You can test by accessing: ${chatServiceUrl}/swagger/index.html`
      );
    }
    throw error;
  }
};
export const updateChatName = async ({ sessionId, title }) => {
  try {
    const response = await chatApi.put(getChatApiRoute(`/chat/sessions/${sessionId}/title`), { title });
    return response.data;
  } catch (error) {
    console.error("Error updating the chat history title:", error);
    throw error;
  }
};
export const getChatMessages = async ({ sessionId }) => {
  try {
    const response = await chatApi.get(getChatApiRoute(`/chat/sessions/${sessionId}/messages`));
    const messages = response.data?.map((message) => {
      const responseData = parseChatResponse(message);
      return {
        id: message.timestamp,
        content: message.text,
        createdAt: new Date(message.timestamp).toISOString(),
        role: message.role?.toLowerCase(),
        ...responseData,
      };
    });
    return messages;
  } catch (error) {
    console.error("Error fetching the chat messages:", error);
    throw error;
  }
};

export const addProviderContact = async (data) => {
  try {
    const response = await api.post("/Identity/ProviderUsers", data);
    return response.data;
  } catch (error) {
    console.error("Error adding provider contact:", error);
    throw error;
  }
};

export const addProviderLocation = async (provider_id, data) => {
  try {
    const response = await api.post(`/Providers/AdditionalLocation/${provider_id}`, data);
    return response.data;
  } catch (error) {
    console.error("Error adding provider location:", error);
    throw error;
  }
};

export const makeRequestContact = async ({
  provider_user_id,
  request_contact_status,
  category,
}) => {
  try {
    console.log("[makeRequestContact] params:", {
      provider_user_id,
      request_contact_status,
      category,
    });
    const response = await api.patch(
      `/Providers/Users/${provider_user_id}/Request-Contact`,
      {
        request_contact_status,
        ...(category ? { category } : {}),
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error making request contact:", error);
    throw error;
  }
};

export const makePrimaryContact = async ({
  provider_id,
  provider_user_id,
  category,
}) => {
  try {
    console.log("[makePrimaryContact] params:", {
      provider_user_id,
      category,
    });
    const qs = category
      ? `?category=${encodeURIComponent(category)}`
      : "";
    const response = await api.patch(
      `/Providers/Users/${provider_id}/Make-Primary/${provider_user_id}${qs}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error making primary contact:", error);
    throw error;
  }
};

/** Organization (vendor) level: hide or show from discovery. */
export const hideOrShowFromDiscovery = async ({ provider_id, is_visible: discovery_status }) => {
  try {
    console.log("[hideOrShowFromDiscovery] params:", { provider_id, discovery_status });
    const response = await api.patch(`/Providers/${provider_id}/Discovery-Status`, { discovery_status });
    return response.data;
  } catch (error) {
    console.error("Error hiding/showing from discovery:", error);
    throw error;
  }
};

/**
 * Maps API response to the UI shape for request stats.
 * When the real endpoint is available, adjust the right-hand side to match API field names.
 */
export function mapRequestsStatsFromApi(data) {
  if (!data) return { total_requests: 0, active_requests: 0 };
  return {
    total_requests: data.total_requests ?? data.totalRequests ?? data.total ?? 0,
    active_requests: data.active_requests ?? data.activeRequests ?? data.active ?? 0,
  };
}

/**
 * Fetches Total Requests and Active Requests stats used in both vendors and sponsors dashboards.
 * Endpoint: GET "Assessments/Admin/RequestMetrics"
 */
export const getRequestMetrics = async () => {
  try {
    const response = await api.get(`Assessments/Admin/RequestMetrics`);
    return mapRequestsStatsFromApi(response.data);
  } catch (error) {
    console.error("Error fetching request metrics:", error);
    throw error;
  }
};



// --- Mocked endpoints (replace URL when available) ---


/** Organization (vendor) level: activate or deactivate provider. */
export const activateOrDeactivateProvider = async ({ provider_id, is_active }) => {
  try {
    console.log("[activateOrDeactivateProvider] params:", { provider_id, is_active });
    const response = await api.patch(`/Providers/${provider_id}`, { active_on_platform: is_active });
    return response.data;
  } catch (error) {
    console.error("Error activating/deactivating provider:", error);
    throw error;
  }
};

export const activateOrDeactivateSponsor = async ({ sponsor_id, is_active }) => {
  try {
    console.log("[activateOrDeactivateSponsor] params:", { sponsor_id, is_active });
    const response = await api.patch(`/Sponsors/Admin/${sponsor_id}`, { is_active });
    return response.data;
  } catch (error) {
    console.error("Error activating/deactivating sponsor:", error);
    throw error;
  }
};

export const activateOrDeactivateProviderUserContact = async ({ provider_user_id, is_active }) => {
  try {
    console.log("[activateOrDeactivateProviderUserContact] params:", { provider_user_id, is_active });
    const response = await api.patch(`/Providers/Users/${provider_user_id}`, { is_active });
    return response.data;
  } catch (error) {
    console.error("Error activating/deactivating provider user contact:", error);
    throw error;
  }
};

export const activateOrDeactivateSponsorUserContact = async ({ sponsor_user_id, is_active }) => {
  try {
    console.log("[activateOrDeactivateSponsorUserContact] params:", { sponsor_user_id, is_active });
    const response = await api.patch(`/SponsorsUser/IntraService/Update/${sponsor_user_id}`, { is_active });
    return response.data;
  } catch (error) {
    console.error("Error activating/deactivating sponsor user contact:", error);
    throw error;
  }
};

export const getRfiScoredSnapshot = async (rfiQuestionnaireId, { runId, publishedOnly } = {}) => {
  try {
    const url = `/RiskScoring/snapshot/${rfiQuestionnaireId}`;
    const params = {};
    if (runId) params.runId = runId;
    if (publishedOnly) params.publishedOnly = true;
    const response = await api.get(url, {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      return null;
    }
    console.error("Error fetching RFI scored snapshot:", error);
    throw error;
  }
};

/**
 * 14030 — Active Risk Scoring Methodology row (RAG thresholds + verbatim
 * anchored 1-5 definitions for Impact / Likelihood / Detectability). FE uses
 * this to render methodology tooltips next to each I/L/D number on the
 * per-question Risk Panel. The copy MUST be rendered verbatim per AC.
 */
export const getRiskMethodology = async () => {
  try {
    const response = await api.get(`/RiskScoring/methodology`);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      return null;
    }
    console.error("Error fetching risk methodology:", error);
    return null;
  }
};

/**
 * 14031 — Admin SME-override on one scored snapshot row. PATCHes tier / score /
 * gap notes; the server stamps reviewer email + timestamp from the bearer
 * token. Returns true on 200, false on 404.
 */
export const patchSmeOverride = async (snapshotId, payload) => {
  try {
    await api.patch(`/RiskScoring/snapshots/${snapshotId}/sme-override`, payload);
    return true;
  } catch (error) {
    if (error?.response?.status === 404) return false;
    console.error("Error patching SME override:", error);
    throw error;
  }
};

/**
 * 14031 — Publish a scoring run with mode-aware validation. Pass
 * requireGapText=true for SME / Both modes; the server returns 422 +
 * { snapshot_ids_missing_gap_text } when any non-Compliant finding has empty
 * gap text. AutoScoreOnly skips validation.
 */
export const publishRunWithValidation = async (scoringRunId, { requireGapText, mode }) => {
  try {
    const response = await api.post(
      `/RiskScoring/runs/${scoringRunId}/publish-with-validation`,
      null,
      { params: { requireGapText: !!requireGapText, mode: mode ?? "Unknown" } }
    );
    return { ok: true, status: response.status, body: response.data };
  } catch (error) {
    if (error?.response?.status === 422) {
      return { ok: false, status: 422, validation: error.response.data };
    }
    console.error("Error publishing scoring run:", error);
    throw error;
  }
};

/**
 * 14031 — Unpublish (retract) a previously-published scoring run. Sponsor +
 * vendor grids immediately revert to "no risk surface yet" for the VQQ.
 */
export const unpublishRun = async (scoringRunId) => {
  try {
    await api.post(`/RiskScoring/runs/${scoringRunId}/retract`);
    return true;
  } catch (error) {
    if (error?.response?.status === 404) return false;
    console.error("Error unpublishing scoring run:", error);
    throw error;
  }
};

/**
 * 14032 — List Diligent Risk Templates. Drives the Template picker on the
 * Custom Risk Model authoring page.
 */
export const listDiligentRiskTemplates = async () => {
  try {
    const response = await api.get(`/RiskScoring/templates`);
    return response.data ?? [];
  } catch (error) {
    console.error("Error listing risk templates:", error);
    return [];
  }
};

/**
 * 14032 — Full question list (id + code + section + base I/L/D) for one
 * Diligent template. Drives the override editor on the authoring page.
 */
export const listTemplateQuestions = async (diligentRiskTemplateId) => {
  if (!diligentRiskTemplateId) return [];
  try {
    const response = await api.get(`/RiskScoring/templates/${diligentRiskTemplateId}/questions`);
    return response.data ?? [];
  } catch (error) {
    console.error("Error listing template questions:", error);
    return [];
  }
};

/**
 * 14032 — Force-rescore a VQQ. Bypasses the idempotency cache (delete-then-
 * rerun). Used after switching the selected risk model in the picker.
 */
export const forceRescoreVqq = async (rfiQuestionnaireId) => {
  try {
    const response = await api.post(`/RiskScoring/score/force`, {
      rfi_questionnaire_id: rfiQuestionnaireId,
    });
    return { ok: true, data: response.data };
  } catch (error) {
    console.error("Error force-rescoring VQQ:", error);
    throw error;
  }
};

/**
 * First-time score for a VQQ that has never been run through the risk
 * engine (typically pre-14030 historical questionnaires). The endpoint is
 * idempotent — if a run already exists it returns from_cache=true rather
 * than re-running, so it's safe to retry.
 */
export const scoreVqq = async (rfiQuestionnaireId) => {
  try {
    const response = await api.post(`/RiskScoring/score`, {
      rfi_questionnaire_id: rfiQuestionnaireId,
    });
    return { ok: true, data: response.data };
  } catch (error) {
    console.error("Error scoring VQQ:", error);
    throw error;
  }
};

/**
 * Save edited Diligent Risk Model base values for a template.
 * `questions` is the list of CHANGED questions only:
 *   [{ diligent_risk_question_id, impact, likelihood, detectability }, ...]
 * The backend writes them through to Airtable (source of truth) and mirrors the
 * accepted edits locally, returning a per-question report
 * ({ updated_count, airtable_written, updated[], skipped[], warning }).
 */
export const updateDiligentTemplateQuestions = async (diligentRiskTemplateId, questions) => {
  try {
    const response = await api.put(
      `/RiskScoring/templates/${diligentRiskTemplateId}/questions`,
      { questions }
    );
    return { ok: true, data: response.data };
  } catch (error) {
    console.error("Error saving Diligent template risk values:", error);
    throw error;
  }
};

/**
 * Trigger an Airtable rubric sync. Hits the admin endpoint that pulls
 * the Diligent Risk Rubric from the configured Airtable base and upserts
 * templates / sections / questions. Returns the sync report so the UI can
 * surface counts.
 */
export const syncRubricFromAirtable = async () => {
  try {
    const response = await api.post(`/RiskScoring/admin/sync-rubric?source=airtable`);
    return { ok: true, data: response.data };
  } catch (error) {
    const status = error?.response?.status;
    const message = error?.response?.data?.detail || error?.response?.data?.message;
    return {
      ok: false,
      status,
      message: message || error?.message || "Airtable sync failed.",
    };
  }
};

/**
 * 14032 — List custom risk models. Pass templateId + sponsorId to scope to a
 * single (template, sponsor) pair (admin authoring + picker case). Omit both
 * to list everything (admin index view).
 */
export const listCustomRiskModels = async ({ templateId, sponsorId, includeInactive } = {}) => {
  try {
    const params = {};
    if (templateId) params.templateId = templateId;
    if (sponsorId) params.sponsorId = sponsorId;
    if (includeInactive) params.includeInactive = true;
    const response = await api.get(`/RiskScoring/custom-models`, { params });
    return response.data ?? [];
  } catch (error) {
    console.error("Error listing custom risk models:", error);
    return [];
  }
};

/**
 * 14032 — Fetch a single custom risk model with its overrides + base I/L/D
 * for the editor.
 */
export const getCustomRiskModel = async (customRiskModelId) => {
  try {
    const response = await api.get(`/RiskScoring/custom-models/${customRiskModelId}`);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    console.error("Error fetching custom risk model:", error);
    throw error;
  }
};

/**
 * 14032 — Create a new custom risk model. Returns { ok, data } on success,
 * { ok:false, conflict, message } on 409 (name already in use within scope).
 */
export const createCustomRiskModel = async (payload) => {
  try {
    const response = await api.post(`/RiskScoring/custom-models`, payload);
    return { ok: true, data: response.data };
  } catch (error) {
    if (error?.response?.status === 409) {
      return { ok: false, conflict: true, message: error.response.data?.detail };
    }
    console.error("Error creating custom risk model:", error);
    throw error;
  }
};

/**
 * 14032 — Replace name + overrides on an existing model. Scope (template,
 * sponsor) is immutable server-side; the editor never lets the admin change
 * those fields, so we always pass them through unchanged.
 */
export const updateCustomRiskModel = async (customRiskModelId, payload) => {
  try {
    const response = await api.put(`/RiskScoring/custom-models/${customRiskModelId}`, payload);
    return { ok: true, data: response.data };
  } catch (error) {
    if (error?.response?.status === 409) {
      return { ok: false, conflict: true, message: error.response.data?.detail };
    }
    if (error?.response?.status === 404) {
      return { ok: false, notFound: true };
    }
    console.error("Error updating custom risk model:", error);
    throw error;
  }
};

/**
 * 14032 — Soft- or hard-delete depending on whether scoring runs reference
 * the model. Returns true on 204, false on 404.
 */
export const deleteCustomRiskModel = async (customRiskModelId) => {
  try {
    await api.delete(`/RiskScoring/custom-models/${customRiskModelId}`);
    return true;
  } catch (error) {
    if (error?.response?.status === 404) return false;
    console.error("Error deleting custom risk model:", error);
    throw error;
  }
};

/**
 * 14032 — Drives the VQQ Details model picker. Returns the stock Diligent
 * Risk Model + every active custom model scoped to this VQQ's (template,
 * sponsor). is_selected is true on the option matching the VQQ's current
 * selection.
 */
export const getAvailableRiskModelsForVqq = async (rfiQuestionnaireId) => {
  try {
    const response = await api.get(`/RiskScoring/questionnaires/${rfiQuestionnaireId}/available-models`);
    return response.data ?? [];
  } catch (error) {
    console.error("Error fetching available risk models for VQQ:", error);
    return [];
  }
};

/**
 * 14032 — Stage a model selection on the VQQ. Does NOT trigger a rescore —
 * admin clicks "Rescore" separately. Pass null custom_risk_model_id to revert
 * to the stock Diligent Risk Model.
 */
export const selectRiskModelForVqq = async (rfiQuestionnaireId, customRiskModelId) => {
  try {
    await api.post(`/RiskScoring/questionnaires/${rfiQuestionnaireId}/select-model`, {
      custom_risk_model_id: customRiskModelId ?? null,
    });
    return { ok: true };
  } catch (error) {
    if (error?.response?.status === 409) {
      return { ok: false, conflict: true, message: error.response.data?.detail };
    }
    console.error("Error selecting risk model for VQQ:", error);
    throw error;
  }
};

/**
 * Ephemeral preview. Score this VQQ as if customRiskModelId were the active
 * model and return the snapshot, persisting nothing. Lets a sponsor (own VQQ)
 * or a Diligent admin preview a different model in the picker while the
 * published run stays untouched. Pass null to preview the stock Diligent Model.
 * Returns the same envelope shape as getRfiScoredSnapshot, with is_preview=true.
 */
export const previewRiskModelForVqq = async (rfiQuestionnaireId, customRiskModelId) => {
  const response = await api.post(
    `/RiskScoring/questionnaires/${rfiQuestionnaireId}/preview-model`,
    { custom_risk_model_id: customRiskModelId ?? null }
  );
  return response.data;
};

/**
 * 14029 — Batch lookup for the Risk Summary grid column. POSTs an array of
 * sponsor_rfi_response_ids and gets back `{ summaries: { [id]: {...} } }`
 * containing the latest published scoring run per VQQ. Ids without a
 * published run are absent from the map (caller renders "--").
 */
export const getRiskSummariesByIds = async (sponsorRfiResponseIds) => {
  const ids = Array.isArray(sponsorRfiResponseIds)
    ? sponsorRfiResponseIds.filter(Boolean)
    : [];
  if (ids.length === 0) return {};
  try {
    const response = await api.post(`/RiskScoring/summaries`, {
      sponsor_rfi_response_ids: ids,
    });
    return response?.data?.summaries ?? {};
  } catch (error) {
    console.error("Error fetching batch risk summaries:", error);
    return {};
  }
};

/** My Vendors — engaged-with + manually-added vendors for the sponsor. */
export const getMyVendors = async (sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.get("/Sponsors/MyVendors/Vendors", { params });
  return response.data;
};

/** My Vendors — add an existing platform vendor via Add Vendor. */
export const addManualMyVendor = async (providerId, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.post(
    "/Sponsors/MyVendors/Vendors",
    { provider_id: providerId },
    { params }
  );
  return response.data;
};

/** My Vendors — remove a manually-added vendor. */
export const removeManualMyVendor = async (providerId, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.delete(`/Sponsors/MyVendors/Vendors/${providerId}`, { params });
  return response.data;
};

/** My Vendors — hide a vendor (moves to Hidden section). */
export const hideMyVendor = async (providerId, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.post(
    `/Sponsors/MyVendors/Vendors/${providerId}/Hidden`,
    null,
    { params }
  );
  return response.data;
};

/** My Vendors — unhide a vendor. */
export const unhideMyVendor = async (providerId, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.delete(
    `/Sponsors/MyVendors/Vendors/${providerId}/Hidden`,
    { params }
  );
  return response.data;
};

/** My Vendors — bookmarked vendor ids for the Bookmarked system list. */
export const getBookmarkedVendors = async (sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.get("/Sponsors/MyVendors/Bookmarks", { params });
  return response.data;
};

export const addBookmarkedVendor = async (providerId, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.post(
    "/Sponsors/MyVendors/Bookmarks",
    { provider_id: providerId },
    { params }
  );
  return response.data;
};

export const removeBookmarkedVendor = async (providerId, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.delete(`/Sponsors/MyVendors/Bookmarks/${providerId}`, { params });
  return response.data;
};

/** My Vendors — sponsor-scoped custom vendor tags (per-vendor-per-sponsor). */
export const getSponsorVendorTags = async (sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.get("/Sponsors/MyVendors/VendorTags", { params });
  return response.data;
};

export const getVendorTagsForProvider = async (providerId, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.get(
    `/Sponsors/MyVendors/Vendors/${providerId}/Tags`,
    { params }
  );
  return response.data;
};

export const setVendorTagsForProvider = async (providerId, tags, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.put(
    `/Sponsors/MyVendors/Vendors/${providerId}/Tags`,
    { tags },
    { params }
  );
  return response.data;
};

/** Global VQQ/VQA notification users configured on the sponsor org (Notifications tab). */
export const getSponsorNotificationUsers = async (sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.get("/Sponsors/NotificationUsers", { params });
  return response.data;
};

export const setSponsorNotificationUsers = async (payload, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.put("/Sponsors/NotificationUsers", payload, { params });
  return response.data;
};

/** Vendor Lists — user-created lists for the Vendor Lists tab. */
export const getVendorLists = async (sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.get("/Sponsors/MyVendors/Lists", { params });
  return response.data;
};

export const createVendorList = async (payload, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.post("/Sponsors/MyVendors/Lists", payload, { params });
  return response.data;
};

export const updateVendorList = async (listId, payload, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.put(`/Sponsors/MyVendors/Lists/${listId}`, payload, { params });
  return response.data;
};

export const duplicateVendorList = async (listId, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.post(
    `/Sponsors/MyVendors/Lists/${listId}/Duplicate`,
    null,
    { params }
  );
  return response.data;
};

export const addVendorListMembers = async (listId, payload, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.post(
    `/Sponsors/MyVendors/Lists/${listId}/Members`,
    payload,
    { params }
  );
  return response.data;
};

export const deleteVendorList = async (listId, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.delete(`/Sponsors/MyVendors/Lists/${listId}`, { params });
  return response.data;
};

export const removeVendorListMember = async (listId, providerId, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.delete(
    `/Sponsors/MyVendors/Lists/${listId}/Members/${providerId}`,
    { params }
  );
  return response.data;
};

export const reorderVendorListMembers = async (listId, payload, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.put(
    `/Sponsors/MyVendors/Lists/${listId}/Members/Order`,
    payload,
    { params }
  );
  return response.data;
};

export const exportVendorListExcel = async (payload) => {
  const response = await api.post("/Sponsors/MyVendors/Lists/Export/Excel", payload);
  return response.data;
};

export const getSystemListMemberOrder = async (systemListKind, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.get(
    `/Sponsors/MyVendors/SystemLists/${encodeURIComponent(systemListKind)}/Members/Order`,
    { params }
  );
  return response.data;
};

export const reorderSystemListMembers = async (systemListKind, payload, sponsorId = null) => {
  const params = sponsorId ? { sponsor_id: sponsorId } : undefined;
  const response = await api.put(
    `/Sponsors/MyVendors/SystemLists/${encodeURIComponent(systemListKind)}/Members/Order`,
    payload,
    { params }
  );
  return response.data;
};